import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlacementAuth } from '../context/PlacementAuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Target, 
  Shield, 
  Award, 
  Lock, 
  CheckCircle, 
  ArrowRight,
  Loader2,
  LogOut,
  Clock,
  AlertTriangle,
  Upload,
  Download,
  ExternalLink,
  FileText,
  Video,
  Phone,
  Mail,
  Briefcase,
  User,
  Zap,
  ChevronRight,
  XCircle,
  Play,
  Calendar,
  Building2,
  Send,
  PhoneCall,
  MessageSquare,
  FileCheck,
  Trophy,
  Coffee,
  Plus,
  Trash2,
  LogIn,
  Info,
  X,
  BarChart3,
  RefreshCcw,
  Link2,
  Linkedin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/placement`;

// 10-Day Outreach Sequence - STRICT EXECUTION
// RULES: No Sundays, No holidays, 9AM-8PM only, Maintain discipline
const OUTREACH_SEQUENCE = {
  1: { 
    action: "LinkedIn: Like + Comment + Connect", 
    icon: <MessageSquare className="h-4 w-4" />, 
    color: "bg-blue-500",
    agentLink: "https://chatgpt.com/share/697b98c7-7670-8009-97e1-3d26c25e07ed",
    doables: [
      "Open the LinkedIn profile of your prospects",
      "Review their last 3 recent posts",
      "Leave a thoughtful, relevant comment on one of their posts",
      "Like 2-3 of their posts to show genuine interest",
      "Send a personalized connection request (NOT a pitch)"
    ],
    tips: [
      "Comment should add value, not just 'Great post!'",
      "Connection note should mention something specific about them"
    ]
  },
  2: { 
    action: "Introductory Cold Email", 
    icon: <Mail className="h-4 w-4" />, 
    color: "bg-green-500",
    agentLink: "https://chatgpt.com/share/697b9add-4ca8-8009-a75a-30792ec601d2",
    doables: [
      "Draft personalized cold email using ChatGPT agent",
      "Keep email brief and value-focused",
      "Send to all Day 1 prospects",
      "Log email with subject line"
    ],
    tips: [
      "Subject line is critical - make it personal",
      "First line should hook them immediately"
    ]
  },
  3: { 
    action: "LinkedIn Message", 
    icon: <MessageSquare className="h-4 w-4" />, 
    color: "bg-blue-500",
    agentLink: "https://chatgpt.com/share/697b98c7-7670-8009-97e1-3d26c25e07ed",
    doables: [
      "Use Day 1 ChatGPT agent for messaging",
      "Send personalized LinkedIn message",
      "Keep message short and conversational",
      "Don't include links in first message",
      "Track responses and engagement"
    ],
    tips: [
      "Reference your previous interaction",
      "Ask a question to start conversation"
    ]
  },
  4: { 
    action: "Cold Call", 
    icon: <PhoneCall className="h-4 w-4" />, 
    color: "bg-orange-500",
    agentLink: "https://chatgpt.com/share/697b9bfa-18e4-8009-b563-9166133eae64",
    doables: [
      "Prepare call script using ChatGPT agent",
      "Call each prospect on your list",
      "Practice objection handling before calls",
      "Log call outcome (connected, voicemail, no answer)",
      "Note any follow-up actions needed"
    ],
    tips: [
      "Best times: 10 AM - 12 PM and 4 PM - 6 PM",
      "Smile while talking - it shows in your voice"
    ]
  },
  5: { 
    action: "First Follow-up Email", 
    icon: <Mail className="h-4 w-4" />, 
    color: "bg-green-500",
    agentLink: "https://chatgpt.com/share/697b9add-4ca8-8009-a75a-30792ec601d2",
    doables: [
      "Use Day 2 ChatGPT agent for follow-up",
      "Reference your initial email",
      "Add new value or insight",
      "Keep it short (2-3 sentences max)",
      "Track who responds"
    ],
    tips: [
      "Don't just ask 'Did you see my email?'",
      "Provide additional value or a resource"
    ]
  },
  6: { 
    action: "First Follow-up Call", 
    icon: <PhoneCall className="h-4 w-4" />, 
    color: "bg-orange-500",
    agentLink: "https://chatgpt.com/share/697b9bfa-18e4-8009-b563-9166133eae64",
    doables: [
      "Use Day 4 ChatGPT agent for call prep",
      "Call prospects who didn't respond",
      "Reference previous touchpoints",
      "Try different times than Day 4",
      "Update tracker with outcomes"
    ],
    tips: [
      "Mention your email and LinkedIn message",
      "Be persistent but not pushy"
    ]
  },
  7: { 
    action: "REST DAY - No Outreach", 
    icon: <Coffee className="h-4 w-4" />, 
    color: "bg-slate-600",
    agentLink: null,
    doables: [
      "Review all responses received so far",
      "Analyze what's working and what's not",
      "Prepare follow-up messages for next days",
      "Rest and recharge"
    ],
    tips: [
      "Use this time to research prospects deeply",
      "Note warm leads for priority follow-up"
    ],
    isRestDay: true
  },
  8: { 
    action: "Second Follow-up Email", 
    icon: <Mail className="h-4 w-4" />, 
    color: "bg-green-500",
    agentLink: "https://chatgpt.com/share/697b9add-4ca8-8009-a75a-30792ec601d2",
    doables: [
      "Use Day 2 ChatGPT agent",
      "Target non-responders",
      "Change the angle or value proposition",
      "Reference something new about their company",
      "Keep tracking all activities"
    ],
    tips: [
      "Show you've been following their news",
      "Try a different subject line approach"
    ]
  },
  9: { 
    action: "Second Follow-up Call", 
    icon: <PhoneCall className="h-4 w-4" />, 
    color: "bg-orange-500",
    agentLink: "https://chatgpt.com/share/697b9bfa-18e4-8009-b563-9166133eae64",
    doables: [
      "Use Day 4 ChatGPT agent",
      "Call at different times than previous",
      "Try mobile if you have it",
      "Be ready with a fresh approach",
      "Document all call attempts"
    ],
    tips: [
      "Try calling before 9 AM or after 5 PM",
      "This may be your last call - make it count"
    ]
  },
  10: { 
    action: "Breakup Email", 
    icon: <Send className="h-4 w-4" />, 
    color: "bg-red-500",
    agentLink: "https://chatgpt.com/share/697b9add-4ca8-8009-a75a-30792ec601d2",
    doables: [
      "Use Day 2 ChatGPT agent for breakup email",
      "Keep it professional and gracious",
      "Thank them for their time",
      "Leave door open for future connection"
    ],
    tips: [
      "Breakup emails often get highest response rate",
      "Don't sound bitter or desperate"
    ],
    isFinal: true
  }
};

// Internship Steps - Updated (6 steps)
const INTERNSHIP_STEPS = {
  1: { name: "Join WhatsApp Group", icon: <MessageSquare className="h-4 w-4" /> },
  2: { name: "Watch Internship Video", icon: <Play className="h-4 w-4" /> },
  3: { name: "Submit LinkedIn Profile", icon: <User className="h-4 w-4" /> },
  4: { name: "Request for Company List", icon: <Building2 className="h-4 w-4" /> },
  5: { name: "Research & Prospect Data", icon: <FileText className="h-4 w-4" /> },
  6: { name: "Submit Internship Metrics", icon: <BarChart3 className="h-4 w-4" /> }
};

// 14-Day Outreach Program (Converted from 21-Day Megamind)
const MEGAMIND_SEQUENCE = {
  // Week 1 - Primary Outreach
  1: { 
    action: "LinkedIn Warm-Up", 
    type: "linkedin", 
    day: "Monday", 
    color: "bg-blue-500", 
    isOutreach: true, 
    description: "View target profiles, like a relevant post, leave thoughtful comments on 2-3 posts, or send a short non-sales connection note. The goal is to appear on their radar without pitching.",
    instructions: [
      "View target profiles on LinkedIn",
      "Like a relevant post from your prospect",
      "Leave thoughtful comments on 2-3 posts",
      "Send a short non-sales connection note",
      "Goal: Appear on their radar WITHOUT pitching"
    ]
  },
  2: { 
    action: "Cold Email", 
    type: "email", 
    day: "Tuesday", 
    color: "bg-green-500", 
    isOutreach: true, 
    description: "Send a personalized cold email to the prospect. Include your portfolio link in the email body (hyperlinked to the word 'portfolio'). Keep the email brief and value-focused.",
    instructions: [
      "Send a personalized cold email to the prospect",
      "Include your portfolio link (hyperlinked to word 'portfolio')",
      "Keep the email brief and value-focused",
      "Focus on how you can add value to them"
    ]
  },
  3: { 
    action: "LinkedIn Outreach", 
    type: "linkedin", 
    day: "Wednesday", 
    color: "bg-blue-500", 
    isOutreach: true, 
    description: "Send a LinkedIn message to the prospect. Keep it short and value-oriented. Avoid including links in the first message if possible.",
    instructions: [
      "Send a LinkedIn message to the prospect",
      "Keep it short and value-oriented",
      "Avoid including links in the first message",
      "Focus on starting a conversation"
    ]
  },
  4: { 
    action: "Gap / Review Day", 
    type: "gap", 
    day: "Thursday", 
    color: "bg-slate-500", 
    isOutreach: false, 
    description: "No outreach. Review responses, analyze messaging effectiveness, and prepare the first follow-up.",
    instructions: [
      "No outreach required today",
      "Review any responses you received",
      "Analyze what messaging worked best",
      "Prepare your first follow-up messages",
      "Update your tracking sheet"
    ]
  },
  5: { 
    action: "First Follow-Up", 
    type: "followup", 
    day: "Friday", 
    color: "bg-amber-500", 
    isOutreach: true, 
    description: "Send the first follow-up via email and LinkedIn. In email: hyperlink the word 'portfolio' to your portfolio. On LinkedIn: mention your portfolio or share a plain URL.",
    instructions: [
      "Send first follow-up via EMAIL",
      "In email: hyperlink word 'portfolio' to your portfolio",
      "Send first follow-up via LINKEDIN",
      "On LinkedIn: mention portfolio or share plain URL",
      "Keep tone friendly and professional"
    ]
  },
  
  // Week 2 - Follow-Ups & Scale
  6: { 
    action: "No Outreach", 
    type: "no_outreach", 
    day: "Monday", 
    color: "bg-slate-600", 
    isOutreach: false, 
    description: "Use this day to review the tracker, respond to inbound messages, and prepare for Tuesday follow-up.",
    instructions: [
      "No outreach required today",
      "Review your tracking sheet",
      "Respond to any inbound messages",
      "Prepare for Tuesday's follow-up"
    ]
  },
  7: { 
    action: "Second Follow-Up", 
    type: "followup", 
    day: "Tuesday", 
    color: "bg-amber-500", 
    isOutreach: true, 
    description: "Send a follow-up asking if they had time to review your portfolio. Keep the tone polite and acknowledge their busy schedule.",
    instructions: [
      "Send follow-up asking if they reviewed your portfolio",
      "Keep the tone polite and professional",
      "Acknowledge their busy schedule",
      "Offer additional value if possible"
    ]
  },
  8: { 
    action: "Gap Day", 
    type: "gap", 
    day: "Wednesday", 
    color: "bg-slate-500", 
    isOutreach: false, 
    description: "No outreach. Review responses and prepare the next follow-up.",
    instructions: [
      "No outreach required today",
      "Review responses received",
      "Prepare next follow-up",
      "Update tracking sheet"
    ]
  },
  9: { 
    action: "Third Follow-Up (Market Research)", 
    type: "followup", 
    day: "Thursday", 
    color: "bg-amber-500", 
    isOutreach: true, 
    description: "Send a follow-up referencing your market research. In the email, hyperlink the words 'market research' to your research document.",
    instructions: [
      "Send follow-up referencing your MARKET RESEARCH",
      "In email: hyperlink 'market research' to your document",
      "Show you've done your homework",
      "Demonstrate industry knowledge"
    ]
  },
  10: { 
    action: "Fourth Follow-Up (Company Research)", 
    type: "followup", 
    day: "Friday", 
    color: "bg-amber-500", 
    isOutreach: true, 
    description: "Send a follow-up referencing research about their company such as product updates, funding, announcements, etc.",
    instructions: [
      "Send follow-up with COMPANY RESEARCH",
      "Reference their product updates",
      "Mention recent funding or announcements",
      "Connect your value to their needs"
    ]
  },
  
  // Days 11-14 - Final Push & Breakup
  11: { 
    action: "No Outreach", 
    type: "no_outreach", 
    day: "Monday", 
    color: "bg-slate-600", 
    isOutreach: false, 
    description: "Use the day for administrative work and tracker updates.",
    instructions: [
      "No outreach required today",
      "Administrative work day",
      "Update all trackers",
      "Prepare for final follow-ups"
    ]
  },
  12: { 
    action: "Fifth Follow-Up (Breakup)", 
    type: "followup", 
    day: "Tuesday", 
    color: "bg-red-500", 
    isOutreach: true, 
    description: "Send the final message acknowledging you haven't heard back, thank them for their time, and leave the door open for future connection.",
    instructions: [
      "Send FINAL breakup message",
      "Acknowledge you haven't heard back",
      "Thank them for their time",
      "Leave the door open for future"
    ]
  },
  13: { 
    action: "Scale Outreach", 
    type: "scale", 
    day: "Wednesday", 
    color: "bg-purple-500", 
    isOutreach: true, 
    description: "Re-run the best performing sequence on new prospects or new companies. Convert to interviews.",
    instructions: [
      "Start fresh sequence with NEW prospects",
      "Use your best performing messages",
      "Push for interview conversions",
      "Apply learnings from previous days"
    ]
  },
  14: { 
    action: "Program Complete / Review", 
    type: "complete", 
    day: "Thursday", 
    color: "bg-emerald-500", 
    isOutreach: false, 
    description: "Submit final report. Choose to repeat the 14-day program for another cycle or exit to the interview stage.",
    instructions: [
      "Submit your final report and outcomes",
      "Review all conversations and responses",
      "Choose: Repeat for another 14 days OR Exit to Interview Stage",
      "Celebrate your progress!"
    ]
  }
};

export default function PlacementAIDashboard() {
  const navigate = useNavigate();
  const { token, logout, isAuthenticated, student: authStudent } = usePlacementAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form states for different steps - Updated workflow (4 steps, starting with Portfolio)
  const [step1Form, setStep1Form] = useState({
    professional_photos_link: '',
    video_resume_link: '',
    certificates_link: '',
    market_research_link: '',
    sales_playbook_link: '',
    cold_call_recording_link: '',
    linkedin_profile_url: '',
    resume_link: '',
    why_hire_me: '',
    past_performance_report: '',  // Renamed from past_performance
    past_experience_company: '',  // New structured field
    past_experience_role: '',     // New structured field
    past_experience_duration: '', // New structured field
    outside_work: '',
    contact_email: '',
    contact_phone: ''
  });
  // Keeping step2Form for backward compatibility
  const [step2Form, setStep2Form] = useState({
    professional_photos_link: '',
    video_resume_link: '',
    certificates_link: '',
    market_research_link: '',
    sales_playbook_link: '',
    cold_call_recording_link: '',
    linkedin_profile_url: '',
    resume_link: '',
    why_hire_me: '',
    past_performance_report: '',
    past_experiences: [{ company: '', role: '', duration: '' }],  // Array of experiences
    outside_work: '',
    contact_email: '',
    contact_phone: ''
  });

  // Functions to manage past experiences
  const addPastExperience = () => {
    setStep2Form(prev => ({
      ...prev,
      past_experiences: [...prev.past_experiences, { company: '', role: '', duration: '' }]
    }));
  };

  const removePastExperience = (index) => {
    if (step2Form.past_experiences.length > 1) {
      setStep2Form(prev => ({
        ...prev,
        past_experiences: prev.past_experiences.filter((_, i) => i !== index)
      }));
    }
  };

  const updatePastExperience = (index, field, value) => {
    setStep2Form(prev => ({
      ...prev,
      past_experiences: prev.past_experiences.map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  // Phase 2 state
  const [internshipData, setInternshipData] = useState(null);
  const [outreachData, setOutreachData] = useState(null);
  const [activeInternshipStep, setActiveInternshipStep] = useState(null);
  const [internshipForm, setInternshipForm] = useState({ link: '', notes: '' });
  const [outreachDayForm, setOutreachDayForm] = useState({ 
    action_completed: false, 
    notes: '', 
    screenshot_link: '', 
    mail_trail_link: '' 
  });
  const [metricsForm, setMetricsForm] = useState({
    companies_outreached: 0,
    total_leads_generated: 0,
    tools_used: '',
    total_linkedin_outreach: 0,
    total_email_outreach: 0,
    total_call_outreach: 0,
    market_focused: '',
    total_hours_spent: 0,
    meetings_set: 0,
    responses_received: 0,
    positive_replies: 0
  });
  const [showMetricsForm, setShowMetricsForm] = useState(false);

  // Phase 3 state
  const [megamindData, setMegamindData] = useState(null);
  const [megamindEligibilityForm, setMegamindEligibilityForm] = useState({
    portfolio_link: '',
    companies_list_link: '',
    prospect_list_link: '',
    market_research_link: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState({
    companies_list: null,
    prospect_list: null,
    messaging_doc: null
  });
  const [uploadingFile, setUploadingFile] = useState(null);
  const [megamindDayForm, setMegamindDayForm] = useState({
    action_completed: false,
    notes: '',
    screenshot_link: '',
    mail_trail_link: '',
    linkedin_screenshot_link: ''
  });
  const [certificateData, setCertificateData] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificateUploadUrl, setCertificateUploadUrl] = useState('');

  // LinkedIn post step state
  const [linkedinPostChecks, setLinkedinPostChecks] = useState({ posted: false, certAdded: false });
  const [linkedinPostSubmitting, setLinkedinPostSubmitting] = useState(false);

  // Interview state
  const [interviews, setInterviews] = useState([]);
  const [showInterviewForm, setShowInterviewForm] = useState(false);
  const [interviewForm, setInterviewForm] = useState({
    interview_date: '',
    company_name: '',
    role: '',
    product: '',
    notes: ''
  });

  // Offer Letter state
  const [offerLetterForm, setOfferLetterForm] = useState({
    company_name: '',
    role: '',
    offer_date: '',
    notes: ''
  });
  const [offerLetterFile, setOfferLetterFile] = useState(null);
  const [uploadingOfferLetter, setUploadingOfferLetter] = useState(false);

  // Attendance state
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/placement-ai');
      return;
    }
    fetchDashboard();
    fetchLeaderboard();
  }, [isAuthenticated, navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
      setInterviews(response.data.interviews || []);
      setAttendanceData(response.data.attendance || null);
      
      // Fetch internship/outreach data if on that path
      if (response.data.decision_path) {
        fetchInternshipStatus();
        fetchOutreachStatus();
        fetchMegamindStatus();
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/placement-ai');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      const response = await axios.get(`${API}/student/attendance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendanceData(response.data);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    }
  };

  const handleAttendanceLogin = async () => {
    setAttendanceLoading(true);
    try {
      const response = await axios.post(`${API}/student/attendance/login`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      toast.info(response.data.reminder, { duration: 5000 });
      fetchAttendance();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to login');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleAttendanceLogout = async () => {
    setAttendanceLoading(true);
    try {
      const response = await axios.post(`${API}/student/attendance/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.attendance_valid) {
        toast.success(response.data.message);
      } else {
        toast.warning(response.data.message);
      }
      fetchAttendance();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to logout');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    setLeaderboardLoading(true);
    try {
      const response = await axios.get(`${API}/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const fetchInternshipStatus = async () => {
    try {
      const response = await axios.get(`${API}/internship/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInternshipData(response.data);
    } catch (error) {
      // May not be on internship path
    }
  };

  const fetchOutreachStatus = async () => {
    try {
      const response = await axios.get(`${API}/outreach/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOutreachData(response.data);
    } catch (error) {
      // May not have started
    }
  };

  const fetchMegamindStatus = async () => {
    try {
      const response = await axios.get(`${API}/megamind/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMegamindData(response.data);
    } catch (error) {
      // May not have started
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/placement-ai');
  };

  const submitStep1 = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/step/1/submit`, step1Form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Portfolio submission successful!');
      fetchDashboard();
      setActiveStep(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Portfolio submission is now Step 1 in the new workflow
      await axios.post(`${API}/step/1/submit`, step2Form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Portfolio submitted!');
      fetchDashboard();
      setActiveStep(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const requestSession = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/step/3/request-session`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('One-on-One session requested!');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCorrections = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/step/4/confirm-corrections`, 
        { corrections_completed: true }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Corrections confirmed!');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Confirmation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const makeDecision = async (choice) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/decision`, 
        { choice }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`You chose ${choice === 'internship' ? 'Internship + Outreach' : 'Direct Outreach'}!`);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Decision failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 2 Functions
  const submitInternshipStep = async (stepNum) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/internship/step/${stepNum}/submit`, internshipForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Internship step ${stepNum} submitted!`);
      setInternshipForm({ link: '', notes: '' });
      setActiveInternshipStep(null);
      fetchInternshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Request company list (Step 4 special workflow)
  const requestCompanyList = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/internship/step/4/request-company-list`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company list requested! Admin will upload it soon.');
      fetchInternshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm company list received and proceed
  const confirmCompanyListReceived = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/internship/step/4/confirm-received`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company list confirmed! Proceeding to next step.');
      setActiveInternshipStep(null);
      fetchInternshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Confirmation failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Request WhatsApp group (Step 1)
  const requestWhatsAppGroup = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/internship/step/1/request-whatsapp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('WhatsApp group request sent! Admin will send you the link.');
      fetchInternshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm video watched (Step 2)
  const confirmVideoWatched = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/internship/step/2/confirm-video`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Video confirmed! Proceeding to next step.');
      setActiveInternshipStep(null);
      fetchInternshipStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Confirmation failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Request WhatsApp switch (outreach)
  const requestWhatsAppSwitch = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/outreach/request-whatsapp-switch`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('WhatsApp switch request sent! Admin will send you the Outreach group link.');
      fetchMegamindStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Repeat 14-day outreach
  const repeatOutreachProgram = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/megamind/repeat`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('14-Day Outreach Program restarted!');
      fetchMegamindStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restart');
    } finally {
      setSubmitting(false);
    }
  };

  // Exit to interview stage
  const exitToInterview = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/megamind/exit-to-interview`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Moved to Interview Stage!');
      fetchDashboard();
      fetchMegamindStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to exit');
    } finally {
      setSubmitting(false);
    }
  };

  const startOutreach = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/outreach/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Outreach sequence started! Day 1 is now active.');
      fetchOutreachStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start outreach');
    } finally {
      setSubmitting(false);
    }
  };

  const submitOutreachDay = async (dayNum) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/outreach/day/${dayNum}/submit`, 
        { ...outreachDayForm, action_completed: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Day ${dayNum} completed!`);
      setOutreachDayForm({ action_completed: false, notes: '', screenshot_link: '', mail_trail_link: '' });
      fetchOutreachStatus();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
      if (error.response?.data?.detail?.includes('locked')) {
        fetchDashboard(); // Refresh to show locked state
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitMetrics = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/internship/metrics`, metricsForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Metrics submitted! You are now eligible for your certificate.');
      setShowMetricsForm(false);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submitLinkedinPostCompletion = async () => {
    setLinkedinPostSubmitting(true);
    try {
      await axios.post(`${API}/linkedin-post-completed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('LinkedIn post confirmed! You can now proceed to Phase 2.');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm');
    } finally {
      setLinkedinPostSubmitting(false);
    }
  };

  // Phase 3 Functions
  // File upload handler for megamind documents
  const handleFileUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedExtensions = ['.xlsx', '.xls', '.csv', '.pdf', '.doc', '.docx'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      toast.error(`Invalid file type. Allowed: ${allowedExtensions.join(', ')}`);
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    
    setUploadingFile(docType);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/megamind/upload-document?doc_type=${docType}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Store the uploaded file info
      setUploadedFiles(prev => ({
        ...prev,
        [docType]: {
          filename: response.data.filename,
          original_filename: response.data.original_filename,
          download_url: response.data.download_url
        }
      }));
      
      // Update form state with the download URL
      const fieldName = `${docType}_file`;
      setMegamindEligibilityForm(prev => ({
        ...prev,
        [fieldName]: response.data.download_url
      }));
      
      toast.success(`${file.name} uploaded successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFile(null);
    }
  };

  const submitMegamindEligibility = async (e) => {
    e.preventDefault();
    
    // Validate all links are provided
    if (!megamindEligibilityForm.portfolio_link) {
      toast.error('Please provide the Portfolio link');
      return;
    }
    if (!megamindEligibilityForm.companies_list_link || !megamindEligibilityForm.prospect_list_link || !megamindEligibilityForm.market_research_link) {
      toast.error('Please provide all required links');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/megamind/eligibility`, {
        portfolio_link: megamindEligibilityForm.portfolio_link,
        companies_list_link: megamindEligibilityForm.companies_list_link,
        prospect_list_link: megamindEligibilityForm.prospect_list_link,
        market_research_link: megamindEligibilityForm.market_research_link
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eligibility links submitted for review!');
      setMegamindEligibilityForm({
        portfolio_link: '',
        companies_list_link: '',
        prospect_list_link: '',
        market_research_link: ''
      });
      fetchMegamindStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const completeMegamindClass = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/megamind/complete-class`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Megamind class marked as completed!');
      fetchMegamindStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const startMegamind = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/megamind/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('14-Day Outreach Program started!');
      fetchMegamindStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start');
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 2 Post-Eligibility Flow handlers
  const phase2WhatsappRequest = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/phase2/whatsapp-request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request sent! Admin will share the WhatsApp group link.');
      fetchMegamindStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request');
    } finally {
      setSubmitting(false);
    }
  };

  const phase2JobProfileRequest = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/phase2/job-profile-request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request sent! Admin will share the Job Profile document.');
      fetchMegamindStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request');
    } finally {
      setSubmitting(false);
    }
  };

  const phase2JobProfileConfirm = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/phase2/job-profile-confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job profiles confirmed! Ready for outreach.');
      fetchMegamindStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm');
    } finally {
      setSubmitting(false);
    }
  };

  const phase2StartOutreach = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/phase2/outreach/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('14-Day Outreach started!');
      fetchMegamindStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start outreach');
    } finally {
      setSubmitting(false);
    }
  };

  const submitPhase2OutreachDay = async (dayNum) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/phase2/outreach/day/${dayNum}/submit`,
        { ...megamindDayForm, action_completed: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Day ${dayNum} completed!`);
      setMegamindDayForm({ action_completed: false, notes: '', screenshot_link: '', mail_trail_link: '', linkedin_screenshot_link: '' });
      fetchMegamindStatus();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submitMegamindDay = async (dayNum) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/megamind/day/${dayNum}/submit`,
        { ...megamindDayForm, action_completed: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Day ${dayNum} completed!`);
      setMegamindDayForm({ action_completed: false, notes: '', screenshot_link: '', mail_trail_link: '', linkedin_screenshot_link: '' });
      fetchMegamindStatus();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
      if (error.response?.data?.detail?.includes('Escalation')) {
        fetchDashboard();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const generateCertificate = async () => {
    setSubmitting(true);
    try {
      const response = await axios.get(`${API}/certificate/generate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificateData(response.data);
      setShowCertificate(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate certificate');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCertificatePDF = async () => {
    setSubmitting(true);
    try {
      const response = await axios.get(`${API}/certificate/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HireForSkillz_Certificate_${authStudent?.name?.replace(' ', '_') || 'Student'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download certificate');
    } finally {
      setSubmitting(false);
    }
  };

  const uploadCertificate = async () => {
    if (!certificateUploadUrl) {
      toast.error('Please enter the certificate URL');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/student/upload-certificate`, 
        { certificate_url: certificateUploadUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Certificate uploaded! You can now proceed to outreach.');
      setCertificateUploadUrl('');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload certificate');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCertificateFromAdmin = async () => {
    if (!dashboard?.certificate_url) {
      toast.error('Certificate not available yet');
      return;
    }
    
    // Download the certificate PDF
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    window.open(`${API_BASE}${dashboard.certificate_url}`, '_blank');
    
    // Mark as downloaded
    try {
      await axios.post(`${API}/student/mark-certificate-downloaded`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Certificate downloaded! You can now proceed to the next level.');
      fetchDashboard();
    } catch (error) {
      console.error('Failed to mark as downloaded:', error);
    }
  };

  // Interview Functions
  const addInterview = async (e) => {
    e.preventDefault();
    if (!interviewForm.interview_date || !interviewForm.company_name || !interviewForm.role || !interviewForm.product) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/interviews`, interviewForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Interview added successfully!');
      setInterviewForm({ interview_date: '', company_name: '', role: '', product: '', notes: '' });
      setShowInterviewForm(false);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add interview');
    } finally {
      setSubmitting(false);
    }
  };

  const requestMockCall = async (interviewId, companyName) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/interviews/${interviewId}/request-mock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Mock call requested for ${companyName}! Admin will contact you soon.`);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request mock call');
    } finally {
      setSubmitting(false);
    }
  };

  const updateInterviewStatus = async (interviewId, status) => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/interviews/${interviewId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Interview status updated!');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update interview');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteInterview = async (interviewId) => {
    if (!window.confirm('Are you sure you want to delete this interview?')) return;
    
    setSubmitting(true);
    try {
      await axios.delete(`${API}/interviews/${interviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Interview deleted');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete interview');
    } finally {
      setSubmitting(false);
    }
  };

  // Offer Letter Upload Function
  const handleOfferLetterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      toast.error(`Invalid file type. Allowed: ${allowedExtensions.join(', ')}`);
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    
    setOfferLetterFile(file);
    toast.success(`${file.name} selected`);
  };

  const submitOfferLetter = async (e) => {
    e.preventDefault();
    
    if (!offerLetterFile) {
      toast.error('Please upload your offer letter');
      return;
    }
    
    if (!offerLetterForm.company_name || !offerLetterForm.role) {
      toast.error('Please fill in company name and role');
      return;
    }
    
    setUploadingOfferLetter(true);
    try {
      // First upload the file
      const formData = new FormData();
      formData.append('file', offerLetterFile);
      
      const uploadResponse = await axios.post(
        `${API}/megamind/upload-offer-letter`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Then submit the offer letter details
      await axios.post(`${API}/megamind/submit-offer-letter`, {
        company_name: offerLetterForm.company_name,
        role: offerLetterForm.role,
        offer_date: offerLetterForm.offer_date,
        notes: offerLetterForm.notes,
        offer_letter_url: uploadResponse.data.download_url
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Congratulations! Your offer letter has been submitted. Admin will review and provide your certificate.');
      setOfferLetterForm({ company_name: '', role: '', offer_date: '', notes: '' });
      setOfferLetterFile(null);
      fetchMegamindStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit offer letter');
    } finally {
      setUploadingOfferLetter(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  // Pending Approval State
  if (dashboard?.status === 'pending_approval') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-amber-500/20">
          <CardContent className="pt-8 text-center">
            <div className="bg-amber-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Pending Approval</h2>
            <p className="text-slate-400 mb-6">
              Your registration is being reviewed by our team. You'll get access once approved.
            </p>
            <p className="text-slate-500 text-sm">Welcome, {dashboard.student_name}</p>
            <Button onClick={handleLogout} variant="outline" className="mt-6 border-white/20 text-white">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Rejected State
  if (dashboard?.status === 'rejected') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-red-500/20">
          <CardContent className="pt-8 text-center">
            <div className="bg-red-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Registration Rejected</h2>
            <p className="text-slate-400 mb-6">
              Your registration has been rejected. Please contact admin for more information.
            </p>
            <Button onClick={handleLogout} variant="outline" className="border-white/20 text-white">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Locked State
  if (dashboard?.status === 'locked') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-red-500/30">
          <CardContent className="pt-8 text-center">
            <div className="bg-red-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Account Locked</h2>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
              <p className="text-red-400 font-medium">
                Discipline determines outcomes.
              </p>
              <p className="text-slate-400 text-sm mt-2">
                Your sequence has been broken. Contact Admin to restart from Day 1.
              </p>
            </div>
            {dashboard.lock_reason && (
              <p className="text-slate-500 text-sm mb-4">Reason: {dashboard.lock_reason}</p>
            )}
            
            {/* Still show scores */}
            {dashboard.scores && (
              <div className="grid grid-cols-2 gap-2 mt-4">
                <div className="bg-white/5 p-3 rounded-lg">
                  <p className="text-slate-400 text-xs">Total Score</p>
                  <p className="text-xl font-bold text-white">{dashboard.scores.total}/100</p>
                </div>
                <div className="bg-white/5 p-3 rounded-lg">
                  <p className="text-slate-400 text-xs">Sequence</p>
                  <p className="text-xl font-bold text-red-400">{dashboard.scores.sequence}/20</p>
                </div>
              </div>
            )}
            
            <Button onClick={handleLogout} variant="outline" className="mt-6 border-white/20 text-white">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active Dashboard
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-2 rounded-lg">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Placement AI</h1>
              <p className="text-xs text-slate-400">Welcome, {dashboard?.student_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={fetchDashboard} 
              variant="ghost" 
              className="text-slate-400 hover:text-white"
              disabled={loading}
              data-testid="header-refresh-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="text-sm font-medium">Refresh</span>
              )}
            </Button>
            <Button onClick={handleLogout} variant="ghost" className="text-slate-400 hover:text-white">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Fixed Action Buttons */}
        <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
          {/* Request Mock Interview Button */}
          <Button 
            onClick={() => {
              setShowInterviewForm(true);
              // Scroll to interviews section
              document.getElementById('upcoming-interviews-section')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg rounded-full px-4 h-12 flex items-center gap-2"
            data-testid="fixed-mock-interview-btn"
          >
            <PhoneCall className="h-5 w-5" />
            <span className="text-sm font-medium">Request Mock Interview</span>
          </Button>
          
          {/* Refresh Button */}
          <div className="flex items-center gap-2 self-end">
            <Button 
              onClick={fetchDashboard} 
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg rounded-full px-5 h-10"
              disabled={loading}
              data-testid="refresh-dashboard-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="text-sm font-semibold">Refresh</span>
              )}
            </Button>
          </div>
        </div>

        {/* Daily Attendance Card - Always at Top */}
        <Card className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border-emerald-500/30 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${attendanceData?.is_logged_in ? 'bg-green-500 animate-pulse' : 'bg-slate-700'}`}>
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Daily Attendance</h3>
                  <p className="text-slate-400 text-sm">
                    {attendanceData?.today ? `Today: ${new Date(attendanceData.today).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}` : 'Loading...'}
                  </p>
                  {attendanceData?.today_record?.login_time && (
                    <p className="text-emerald-400 text-xs mt-1">
                      Logged in at {new Date(attendanceData.today_record.login_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Stats */}
                <div className="text-center px-4 border-r border-white/10">
                  <p className="text-2xl font-bold text-emerald-400">{attendanceData?.total_days_completed || 0}</p>
                  <p className="text-xs text-slate-400">Days Completed</p>
                </div>
                
                {/* Login/Logout Buttons */}
                <div className="flex gap-2">
                  {!attendanceData?.is_logged_in && !attendanceData?.today_record?.status?.includes('completed') ? (
                    <Button
                      onClick={handleAttendanceLogin}
                      disabled={attendanceLoading || attendanceData?.today_record?.status === 'completed'}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid="attendance-login-btn"
                    >
                      {attendanceLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <LogIn className="h-4 w-4 mr-2" />
                      )}
                      Login
                    </Button>
                  ) : attendanceData?.is_logged_in ? (
                    <Button
                      onClick={handleAttendanceLogout}
                      disabled={attendanceLoading}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="attendance-logout-btn"
                    >
                      {attendanceLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <LogOut className="h-4 w-4 mr-2" />
                      )}
                      Logout
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        {attendanceData?.today_record?.hours_worked?.toFixed(1)}h logged today
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Info Banner */}
            <div className="mt-4 bg-white/5 rounded-lg p-3 text-sm text-slate-400">
              <p className="flex items-center gap-2">
                <Info className="h-4 w-4 text-emerald-400" />
                Login and logout daily. Minimum <span className="text-emerald-400 font-medium">6 hours</span> between login and logout required for attendance credit.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard Preview Card */}
        <Card 
          className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-500/30 mb-6 hover:from-amber-900/50 hover:to-orange-900/50 transition-all cursor-pointer"
          onClick={() => setShowLeaderboard(true)}
          data-testid="leaderboard-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-3 rounded-xl">
                  <Trophy className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Leaderboard</h3>
                  <p className="text-slate-400 text-sm">
                    {leaderboard?.current_user_rank ? (
                      <>Your Rank: <span className="text-amber-400 font-bold">#{leaderboard.current_user_rank}</span> of {leaderboard.total_participants}</>
                    ) : 'See how you rank against others'}
                  </p>
                </div>
              </div>
              
              {/* Top 3 Preview */}
              {leaderboard?.leaderboard?.slice(0, 3).length > 0 && (
                <div className="hidden md:flex items-center gap-2">
                  {leaderboard.leaderboard.slice(0, 3).map((entry, idx) => (
                    <div 
                      key={entry.student_id} 
                      className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                        idx === 0 ? 'bg-amber-500/20 text-amber-400' :
                        idx === 1 ? 'bg-slate-400/20 text-slate-300' :
                        'bg-orange-500/20 text-orange-400'
                      }`}
                    >
                      <span>{idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}</span>
                      <span className="max-w-20 truncate">{entry.name.split(' ')[0]}</span>
                    </div>
                  ))}
                </div>
              )}
              
              <ChevronRight className="h-5 w-5 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        {/* Scoreboard - Always Visible */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-amber-500" />
            Dynamic Scoreboard
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/20">
              <CardContent className="pt-4 pb-4">
                <p className="text-blue-400 text-xs font-medium mb-1">Compliance</p>
                <p className="text-2xl font-bold text-white">{dashboard?.scores?.compliance || 0}<span className="text-slate-500 text-sm">/30</span></p>
                <Progress value={(dashboard?.scores?.compliance || 0) / 30 * 100} className="h-1 mt-2" />
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/20">
              <CardContent className="pt-4 pb-4">
                <p className="text-green-400 text-xs font-medium mb-1">Sequence</p>
                <p className="text-2xl font-bold text-white">{dashboard?.scores?.sequence || 0}<span className="text-slate-500 text-sm">/20</span></p>
                <Progress value={(dashboard?.scores?.sequence || 0) / 20 * 100} className="h-1 mt-2" />
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/20">
              <CardContent className="pt-4 pb-4">
                <p className="text-purple-400 text-xs font-medium mb-1">Messaging</p>
                <p className="text-2xl font-bold text-white">{dashboard?.scores?.messaging || 0}<span className="text-slate-500 text-sm">/25</span></p>
                <Progress value={(dashboard?.scores?.messaging || 0) / 25 * 100} className="h-1 mt-2" />
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 border-orange-500/20">
              <CardContent className="pt-4 pb-4">
                <p className="text-orange-400 text-xs font-medium mb-1">Outcomes</p>
                <p className="text-2xl font-bold text-white">{dashboard?.scores?.outcomes || 0}<span className="text-slate-500 text-sm">/25</span></p>
                <Progress value={(dashboard?.scores?.outcomes || 0) / 25 * 100} className="h-1 mt-2" />
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/20">
              <CardContent className="pt-4 pb-4">
                <p className="text-amber-400 text-xs font-medium mb-1">Total Score</p>
                <p className="text-2xl font-bold text-white">{dashboard?.scores?.total || 0}<span className="text-slate-500 text-sm">/100</span></p>
                <Progress value={(dashboard?.scores?.total || 0)} className="h-1 mt-2" />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Workflow Steps */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-500" />
            Gated Workflow
          </h2>
          
          <div className="space-y-4">
            {dashboard?.steps && Object.entries(dashboard.steps)
              .filter(([stepNum]) => parseInt(stepNum) !== 2)  // Skip Step 2 (Admin Review removed)
              .map(([stepNum, step]) => (
              <StepCard
                key={stepNum}
                stepNum={parseInt(stepNum)}
                step={step}
                currentStep={dashboard.current_step}
                isActive={activeStep === parseInt(stepNum)}
                onToggle={() => setActiveStep(activeStep === parseInt(stepNum) ? null : parseInt(stepNum))}
                onSubmitStep1={submitStep1}
                onSubmitStep2={submitStep2}
                onRequestSession={requestSession}
                onConfirmCorrections={confirmCorrections}
                step1Form={step1Form}
                setStep1Form={setStep1Form}
                step2Form={step2Form}
                setStep2Form={setStep2Form}
                submitting={submitting}
                addPastExperience={addPastExperience}
                removePastExperience={removePastExperience}
                updatePastExperience={updatePastExperience}
              />
            ))}

            {/* Decision Node */}
            {dashboard?.current_step === 6 && !dashboard?.decision_path && (
              <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <div className="bg-amber-500 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold">
                      6
                    </div>
                    Decision Node
                    <Badge className="bg-amber-500 text-white ml-2">CHOOSE YOUR PATH</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-400 mb-6">
                    Congratulations on completing all steps! Now choose your path:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Button
                      onClick={() => makeDecision('internship')}
                      disabled={submitting}
                      className="h-auto py-6 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 flex flex-col items-center"
                    >
                      <Briefcase className="h-8 w-8 mb-2" />
                      <span className="text-lg font-semibold">Internship + Outreach</span>
                      <span className="text-xs text-blue-200 mt-1">6 steps then outreach</span>
                    </Button>
                    <Button
                      onClick={() => makeDecision('direct_outreach')}
                      disabled={submitting}
                      className="h-auto py-6 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 flex flex-col items-center"
                    >
                      <Target className="h-8 w-8 mb-2" />
                      <span className="text-lg font-semibold">Direct Outreach</span>
                      <span className="text-xs text-purple-200 mt-1">Start outreach immediately</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Decision Made */}
            {dashboard?.decision_path && (
              <Card className="bg-green-500/10 border-green-500/30">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                    <div>
                      <h3 className="text-white font-semibold">Path Selected</h3>
                      <p className="text-slate-400">
                        You chose: <span className="text-green-400 font-medium">
                          {dashboard.decision_path === 'internship' ? 'Internship + Outreach' : 'Direct Outreach'}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white/5 rounded-lg">
                    <p className="text-slate-400 text-sm">
                      {dashboard.decision_path === 'internship' 
                        ? 'Complete internship steps 1-5, then the 10-day outreach, and submit your metrics.'
                        : 'You can now proceed with the 14-Day Outreach Program.'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Phase 2: Internship Section */}
        {dashboard?.decision_path === 'internship' && internshipData && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-blue-500" />
              Internship Program
            </h2>
            
            {/* Monday Start Disclaimer */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4" data-testid="monday-disclaimer-internship">
              <p className="text-amber-300 text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                Always start Internship on a Monday for best results and proper tracking.
              </p>
            </div>
            
            <div className="space-y-3">
              {Object.entries(INTERNSHIP_STEPS)
              .filter(([stepNum]) => parseInt(stepNum) !== 6)  // Step 6 (Metrics) handled separately after outreach
              .map(([stepNum, step]) => {
                const num = parseInt(stepNum);
                const currentStep = internshipData.internship?.current_step || 1;
                const completedSteps = internshipData.internship?.completed_steps || [];
                const isCompleted = completedSteps.includes(num);
                const internshipStarted = internshipData.internship?.internship_started;
                const internshipCompleted = internshipData.internship?.internship_completed;
                
                // Step 6 is locked until admin marks internship as complete
                const isActive = num === 6 
                  ? (internshipCompleted && !isCompleted && currentStep === 6)
                  : (num === currentStep && !isCompleted);
                const isLocked = num === 6 
                  ? (!internshipCompleted || currentStep < 6) && !isCompleted
                  : (num > currentStep && !isCompleted);
                // Step 5 stays at "Internship In Progress" after being completed if internship not done
                const isInternshipInProgress = num === 5 && completedSteps.includes(5) && internshipStarted && !internshipCompleted;
                
                const submission = internshipData.internship?.submissions?.[`step_${num}`];
                const whatsappStatus = internshipData.internship?.whatsapp_status;
                const videoWatched = internshipData.internship?.video_watched;
                
                return (
                  <Card key={num} className={`
                    ${isLocked ? 'bg-slate-800/50 border-slate-700 opacity-60' : ''}
                    ${isActive ? 'bg-slate-800 border-blue-500/50' : ''}
                    ${isCompleted ? 'bg-slate-800 border-green-500/30' : ''}
                  `} data-testid={`internship-step-${num}`}>
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer"
                      onClick={() => !isLocked && setActiveInternshipStep(activeInternshipStep === num ? null : num)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`
                          w-10 h-10 rounded-full flex items-center justify-center
                          ${isCompleted ? 'bg-green-500/20' : ''}
                          ${isActive ? 'bg-blue-500' : ''}
                          ${isLocked ? 'bg-slate-700' : ''}
                        `}>
                          {isCompleted ? <CheckCircle className="h-5 w-5 text-green-500" /> : 
                           isLocked ? <Lock className="h-5 w-5 text-slate-500" /> : step.icon}
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{step.name}</h3>
                          <p className="text-slate-400 text-sm">Internship Step {num}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {isCompleted && !isInternshipInProgress && <Badge className="bg-green-500">Completed</Badge>}
                        {isInternshipInProgress && <Badge className="bg-purple-500">Outreach In Progress</Badge>}
                        {submission?.status === 'pending_review' && <Badge className="bg-amber-500">Pending Review</Badge>}
                        {num === 1 && whatsappStatus === 'requested' && !isCompleted && <Badge className="bg-amber-500">Requested</Badge>}
                        {num === 6 && isLocked && internshipStarted && !internshipCompleted && <Badge className="bg-slate-500">Complete Outreach First</Badge>}
                        {isActive && !submission && num !== 1 && <Badge className="bg-blue-500">Active</Badge>}
                        {isActive && num === 1 && !whatsappStatus && <Badge className="bg-blue-500">Active</Badge>}
                        {!isLocked && <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${activeInternshipStep === num ? 'rotate-90' : ''}`} />}
                      </div>
                    </div>
                    
                    {/* Step 1: Join WhatsApp Group */}
                    {activeInternshipStep === num && num === 1 && !whatsappStatus && isActive && (
                      <CardContent className="pt-0 pb-6 border-t border-white/10">
                        <div className="mt-4 space-y-4">
                          <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                            <h4 className="text-green-400 font-medium mb-2 flex items-center gap-2">
                              <MessageSquare className="h-4 w-4" />
                              Join Internship WhatsApp Group
                            </h4>
                            <p className="text-slate-300 text-sm mb-4">
                              Click the button below to request access to the Internship WhatsApp Group. 
                              Admin will manually send you the group link.
                            </p>
                            <Button 
                              onClick={requestWhatsAppGroup} 
                              disabled={submitting}
                              className="bg-green-500 hover:bg-green-600"
                              data-testid="request-whatsapp-btn"
                            >
                              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                              Request to Join
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    )}
                    
                    {/* Step 1: WhatsApp - Requested, waiting for admin */}
                    {activeInternshipStep === num && num === 1 && whatsappStatus === 'requested' && (
                      <CardContent className="pt-0 pb-6 border-t border-white/10">
                        <div className="mt-4 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                          <p className="text-amber-400 flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Request sent! Waiting for admin to send WhatsApp group link...
                          </p>
                        </div>
                      </CardContent>
                    )}
                    
                    {/* Step 2: Watch Internship Video */}
                    {activeInternshipStep === num && num === 2 && isActive && !videoWatched && (
                      <CardContent className="pt-0 pb-6 border-t border-white/10">
                        <div className="mt-4 space-y-4">
                          <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                            <h4 className="text-purple-400 font-medium mb-2 flex items-center gap-2">
                              <Play className="h-4 w-4" />
                              Did you watch the Internship Process Explanation Video?
                            </h4>
                            <p className="text-slate-300 text-sm mb-4">
                              Watch the complete Internship explanation video before proceeding. 
                              Click "Yes, I Watched" only after you have finished watching the entire video.
                            </p>
                            <Button 
                              onClick={confirmVideoWatched} 
                              disabled={submitting}
                              className="bg-purple-500 hover:bg-purple-600"
                              data-testid="confirm-video-btn"
                            >
                              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                              Yes, I Watched the Video
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    )}
                    
                    {/* Step 3 & 5: Regular link submission (LinkedIn Profile / Research Prospect Data) */}
                    {activeInternshipStep === num && isActive && !submission?.status && (num === 3 || num === 5 || num === 6) && (
                      <CardContent className="pt-0 pb-6 border-t border-white/10">
                        <div className="mt-4 space-y-4">
                          {num === 5 && (
                            <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                              <p className="text-blue-300 text-sm">Research Prospect Data (Paste the same sheet link here)</p>
                            </div>
                          )}
                          <div>
                            <Label className="text-slate-300">{num === 3 ? 'LinkedIn Profile URL' : num === 5 ? 'Sheet Link' : 'Metrics Link/Notes'}</Label>
                            <Input
                              type="url"
                              value={internshipForm.link}
                              onChange={(e) => setInternshipForm({...internshipForm, link: e.target.value})}
                              className="bg-white/10 border-white/20 text-white"
                              placeholder={num === 3 ? 'https://linkedin.com/in/...' : 'https://docs.google.com/...'}
                              data-testid={`internship-step-${num}-link`}
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300">Notes (Optional)</Label>
                            <Textarea
                              value={internshipForm.notes}
                              onChange={(e) => setInternshipForm({...internshipForm, notes: e.target.value})}
                              className="bg-white/10 border-white/20 text-white"
                              placeholder="Any additional notes..."
                            />
                          </div>
                          <Button 
                            onClick={() => submitInternshipStep(num)} 
                            disabled={submitting || !internshipForm.link}
                            className="bg-blue-500 hover:bg-blue-600"
                            data-testid={`submit-internship-step-${num}`}
                          >
                            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                            Submit Step {num}
                          </Button>
                        </div>
                      </CardContent>
                    )}
                    
                    {/* Step 4: Company List Request - Special Workflow */}
                    {activeInternshipStep === num && num === 4 && !submission?.status && isActive && (
                      <CardContent className="pt-0 pb-6 border-t border-white/10">
                        <div className="mt-4 space-y-4">
                          <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                            <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
                              <Building2 className="h-4 w-4" />
                              Request Company List
                            </h4>
                            <p className="text-slate-300 text-sm mb-4">
                              Click the button below to request your personalized company list from the admin. 
                              Once the admin uploads the list, you'll be able to download it here.
                            </p>
                            <Button 
                              onClick={requestCompanyList} 
                              disabled={submitting}
                              className="bg-blue-500 hover:bg-blue-600"
                              data-testid="request-company-list-btn"
                            >
                              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                              Request Company List
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    )}
                    
                    {/* Step 4: Company List - Requested but waiting for admin */}
                    {activeInternshipStep === num && num === 4 && submission?.status === 'requested' && (
                      <CardContent className="pt-0 pb-6 border-t border-white/10">
                        <div className="mt-4 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                          <p className="text-amber-400 flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Company list requested. Waiting for admin to upload...
                          </p>
                          <p className="text-slate-400 text-sm mt-2">
                            Requested on: {submission.requested_at ? new Date(submission.requested_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </CardContent>
                    )}
                    
                    {/* Step 4: Company List - Ready to download */}
                    {activeInternshipStep === num && num === 4 && submission?.status === 'ready' && (
                      <CardContent className="pt-0 pb-6 border-t border-white/10">
                        <div className="mt-4 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                          <p className="text-green-400 flex items-center gap-2 mb-3">
                            <CheckCircle className="h-4 w-4" />
                            Your company list is ready!
                          </p>
                          <a 
                            href={submission.company_list_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
                          >
                            <Download className="h-4 w-4" />
                            Download Company List
                          </a>
                          {submission.admin_notes && (
                            <p className="text-slate-400 text-sm mt-3">
                              <span className="text-slate-300">Admin notes:</span> {submission.admin_notes}
                            </p>
                          )}
                          <Button 
                            onClick={confirmCompanyListReceived} 
                            disabled={submitting}
                            className="mt-4 bg-blue-500 hover:bg-blue-600"
                            data-testid="confirm-company-list-btn"
                          >
                            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                            Confirm & Proceed to Next Step
                          </Button>
                        </div>
                      </CardContent>
                    )}
                    
                    {/* Pending review message */}
                    {activeInternshipStep === num && submission?.status === 'pending_review' && (
                      <CardContent className="pt-0 pb-6 border-t border-white/10">
                        <div className="mt-4 p-4 bg-amber-500/10 rounded-lg">
                          <p className="text-amber-400">Submission pending admin review</p>
                          {submission.link && (
                            <a href={submission.link} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline text-sm flex items-center gap-1 mt-2">
                              <ExternalLink className="h-3 w-3" /> View Submission
                            </a>
                          )}
                        </div>
                      </CardContent>
                    )}
                    
                    {/* Rejected / Needs resubmission */}
                    {activeInternshipStep === num && submission?.status === 'rejected' && submission?.needs_resubmit && (
                      <CardContent className="pt-0 pb-6 border-t border-white/10">
                        <div className="mt-4 p-4 bg-red-500/10 rounded-lg border border-red-500/20 mb-4">
                          <p className="text-red-400 flex items-center gap-2">
                            <XCircle className="h-4 w-4" />
                            Needs resubmission. Please review feedback and resubmit.
                          </p>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <Label className="text-slate-300">Updated Link</Label>
                            <Input
                              type="url"
                              value={internshipForm.link}
                              onChange={(e) => setInternshipForm({...internshipForm, link: e.target.value})}
                              className="bg-white/10 border-white/20 text-white"
                              placeholder="https://..."
                            />
                          </div>
                          <Button 
                            onClick={() => submitInternshipStep(num)} 
                            disabled={submitting || !internshipForm.link}
                            className="bg-blue-500 hover:bg-blue-600"
                          >
                            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                            Resubmit Step {num}
                          </Button>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* 10-Day Outreach Section for INTERNSHIP path - Show after internship step 5 approved */}
        {dashboard?.decision_path === 'internship' && 
         internshipData?.internship?.internship_started && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Send className="h-5 w-5 text-orange-500" />
              {megamindData?.megamind?.started_at ? 'Phase 1: ' : ''}Internship Outreach (10 Days)
              {outreachData?.outreach?.is_active && !outreachData?.is_completed && (
                <Badge className="bg-orange-500 ml-2">IN PROGRESS</Badge>
              )}
              {outreachData?.is_completed && (
                <Badge className="bg-green-500 ml-2">COMPLETED</Badge>
              )}
            </h2>
            
            {/* Start Outreach Button */}
            {!outreachData?.outreach?.started_at && (
              <Card className="bg-slate-800 border-orange-500/30 mb-4">
                <CardContent className="pt-6">
                  <div className="text-center py-4">
                    <Play className="h-10 w-10 text-orange-500 mx-auto mb-3" />
                    <p className="text-slate-300 mb-4">Ready to start your 10-day outreach sequence?</p>
                    <Button 
                      onClick={startOutreach} 
                      disabled={submitting}
                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                    >
                      {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                      Start Outreach Sequence
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Outreach Days Grid */}
            {outreachData?.outreach?.started_at && (
              <>
                {/* Important Rules Banner */}
                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-amber-500/20 p-2 rounded-lg flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-amber-300 font-semibold mb-1">IMPORTANT OUTREACH RULES (NON-NEGOTIABLE)</h4>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="text-slate-300">• No Sundays</span>
                        <span className="text-slate-300">• No Holidays</span>
                        <span className="text-slate-300">• <span className="text-amber-300">9 AM - 8 PM</span> only</span>
                        <span className="text-slate-300">• Maintain discipline</span>
                      </div>
                      <p className="text-slate-500 text-xs mt-2">
                        This internship is performance-driven, discipline-driven, and execution-driven. Your continuation depends on following the process.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(OUTREACH_SEQUENCE).map(([dayNum, day]) => {
                  const num = parseInt(dayNum);
                  const currentDay = outreachData.outreach?.current_day || 1;
                  const completedDays = outreachData.outreach?.completed_days || [];
                  const isCompleted = completedDays.includes(num);
                  const isActive = num === currentDay && !isCompleted;
                  const isLocked = num > currentDay;
                  
                  return (
                    <Card 
                      key={num} 
                      className={`
                        cursor-pointer transition-all
                        ${isLocked ? 'bg-slate-800/50 border-slate-700 opacity-50' : ''}
                        ${isActive ? 'bg-slate-800 border-orange-500 ring-2 ring-orange-500/50' : ''}
                        ${isCompleted ? 'bg-green-500/10 border-green-500/30' : ''}
                      `}
                      onClick={() => isActive && setActiveStep(activeStep === `outreach_${num}` ? null : `outreach_${num}`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-bold ${isCompleted ? 'text-green-400' : isActive ? 'text-orange-400' : 'text-slate-500'}`}>
                            Day {num}
                          </span>
                          {isCompleted && <CheckCircle className="h-4 w-4 text-green-500" />}
                          {isLocked && <Lock className="h-3 w-3 text-slate-500" />}
                        </div>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${day.color}`}>
                          {day.icon}
                        </div>
                        <p className={`text-xs ${isCompleted ? 'text-green-400' : isActive ? 'text-white' : 'text-slate-400'}`}>
                          {day.action}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              </>
            )}

            {/* Active Day Submission Form with Instructions */}
            {outreachData?.outreach?.started_at && typeof activeStep === 'string' && activeStep.startsWith('outreach_') && (
              <Card className="mt-4 bg-slate-800 border-orange-500/30">
                {(() => {
                  const dayNum = parseInt(activeStep.split('_')[1]);
                  const dayData = OUTREACH_SEQUENCE[dayNum];
                  return (
                    <>
                      <CardHeader className="pb-4">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-white flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${dayData.color}`}>
                              {dayData.icon}
                            </div>
                            Day {dayNum}: {dayData.action}
                          </CardTitle>
                          {dayData.isRestDay && (
                            <Badge className="bg-slate-600 text-slate-200">Rest Day</Badge>
                          )}
                          {dayData.isFinal && (
                            <Badge className="bg-red-500 text-white">Final Day</Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-6">
                          {/* Today's Instructions Section */}
                          <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl p-5 border border-purple-500/30">
                            <h4 className="text-lg font-semibold text-purple-300 mb-4 flex items-center gap-2">
                              <FileText className="h-5 w-5" />
                              Today's Instructions
                            </h4>
                            
                            {/* Doables */}
                            <div className="space-y-3">
                              <p className="text-sm font-medium text-slate-300 uppercase tracking-wide">What to do:</p>
                              <div className="space-y-2">
                                {dayData.doables?.map((doable, idx) => (
                                  <div key={idx} className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
                                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/30 text-purple-300 flex items-center justify-center text-sm font-bold">
                                      {idx + 1}
                                    </span>
                                    <span className="text-slate-200">{doable}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Tips */}
                            {dayData.tips && dayData.tips.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-purple-500/20">
                                <p className="text-sm font-medium text-amber-300 mb-2 flex items-center gap-2">
                                  <Zap className="h-4 w-4" />
                                  Pro Tips:
                                </p>
                                <ul className="space-y-1">
                                  {dayData.tips.map((tip, idx) => (
                                    <li key={idx} className="text-slate-400 text-sm flex items-start gap-2">
                                      <span className="text-amber-400">•</span>
                                      {tip}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Agent Link */}
                            {dayData.agentLink && (
                              <div className="mt-4 pt-4 border-t border-purple-500/20">
                                <a 
                                  href={dayData.agentLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-4 py-2 rounded-lg font-medium transition-all"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                  Open ChatGPT Agent for This Day
                                </a>
                                <p className="text-slate-500 text-xs mt-2">
                                  Use this AI agent to help you craft your messages and scripts
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {/* Important Rules Reminder */}
                          <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
                            <h5 className="text-amber-400 font-semibold mb-2 flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4" />
                              Important Rules (NON-NEGOTIABLE)
                            </h5>
                            <ul className="text-slate-400 text-sm space-y-1">
                              <li>• No outreach on Sundays</li>
                              <li>• No outreach on public holidays</li>
                              <li>• Outreach only between <span className="text-amber-300 font-medium">9:00 AM – 8:00 PM</span></li>
                              <li>• Maintain discipline and consistency</li>
                            </ul>
                          </div>
                          
                          {/* Submission Form */}
                          <div className="pt-4 border-t border-white/10">
                            <h5 className="text-white font-semibold mb-4">Mark Day as Complete</h5>
                            <div className="space-y-4">
                              <div>
                                <Label className="text-slate-300">What did you accomplish? *</Label>
                                <Textarea
                                  value={outreachDayForm.notes}
                                  onChange={(e) => setOutreachDayForm({...outreachDayForm, notes: e.target.value})}
                                  className="bg-white/10 border-white/20 text-white"
                                  placeholder="Describe what you did today... (e.g., 'Sent 5 LinkedIn connection requests, liked and commented on 3 posts')"
                                  rows={3}
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-slate-300">Screenshot Link (Optional)</Label>
                                  <Input
                                    type="url"
                                    value={outreachDayForm.screenshot_link}
                                    onChange={(e) => setOutreachDayForm({...outreachDayForm, screenshot_link: e.target.value})}
                                    className="bg-white/10 border-white/20 text-white"
                                    placeholder="https://drive.google.com/..."
                                  />
                                </div>
                                <div>
                                  <Label className="text-slate-300">Mail Trail Link (Optional)</Label>
                                  <Input
                                    type="url"
                                    value={outreachDayForm.mail_trail_link}
                                    onChange={(e) => setOutreachDayForm({...outreachDayForm, mail_trail_link: e.target.value})}
                                    className="bg-white/10 border-white/20 text-white"
                                    placeholder="https://..."
                                  />
                                </div>
                              </div>
                              <Button 
                                onClick={() => submitOutreachDay(dayNum)}
                                disabled={submitting || !outreachDayForm.notes.trim()}
                                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 h-12 text-lg"
                              >
                                {submitting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CheckCircle className="mr-2 h-5 w-5" />}
                                Complete Day {dayNum}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </>
                  );
                })()}
              </Card>
            )}

            {/* Outreach Completed - Waiting for Admin to Mark Internship Complete */}
            {outreachData?.is_completed && dashboard.decision_path === 'internship' && !internshipData?.internship?.internship_completed && (
              <Card className="mt-4 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-amber-500/30">
                <CardContent className="pt-6">
                  <div className="text-center py-4">
                    <Clock className="h-12 w-12 text-amber-400 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white mb-2">10-Day Outreach Complete!</h3>
                    <p className="text-slate-400 mb-2">Waiting for admin to review and mark your internship as complete.</p>
                    <p className="text-slate-500 text-sm">Once approved, you'll be able to submit your final internship metrics.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Outreach Completed + Internship Marked Complete - Show Metrics Form */}
            {outreachData?.is_completed && dashboard.decision_path === 'internship' && internshipData?.internship?.internship_completed && !dashboard.certificate_status && (
              <Card className="mt-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
                <CardContent className="pt-6">
                  <div className="text-center mb-6">
                    <Trophy className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white">Internship Outreach Complete!</h3>
                    <p className="text-slate-400">Submit your final metrics to get your certificate</p>
                  </div>
                  
                  {!showMetricsForm ? (
                    <Button 
                      onClick={() => setShowMetricsForm(true)}
                      className="w-full bg-green-500 hover:bg-green-600"
                    >
                      <Award className="mr-2 h-5 w-5" />
                      Submit Final Metrics
                    </Button>
                  ) : (
                    <form onSubmit={submitMetrics} className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-slate-300">Companies Outreached</Label>
                          <Input
                            type="number"
                            value={metricsForm.companies_outreached}
                            onChange={(e) => setMetricsForm({...metricsForm, companies_outreached: parseInt(e.target.value) || 0})}
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Total Leads Generated</Label>
                          <Input
                            type="number"
                            value={metricsForm.total_leads_generated}
                            onChange={(e) => setMetricsForm({...metricsForm, total_leads_generated: parseInt(e.target.value) || 0})}
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Meetings Set</Label>
                          <Input
                            type="number"
                            value={metricsForm.meetings_set}
                            onChange={(e) => setMetricsForm({...metricsForm, meetings_set: parseInt(e.target.value) || 0})}
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">LinkedIn Outreach</Label>
                          <Input
                            type="number"
                            value={metricsForm.total_linkedin_outreach}
                            onChange={(e) => setMetricsForm({...metricsForm, total_linkedin_outreach: parseInt(e.target.value) || 0})}
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Email Outreach</Label>
                          <Input
                            type="number"
                            value={metricsForm.total_email_outreach}
                            onChange={(e) => setMetricsForm({...metricsForm, total_email_outreach: parseInt(e.target.value) || 0})}
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Call Outreach</Label>
                          <Input
                            type="number"
                            value={metricsForm.total_call_outreach}
                            onChange={(e) => setMetricsForm({...metricsForm, total_call_outreach: parseInt(e.target.value) || 0})}
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Hours Spent</Label>
                          <Input
                            type="number"
                            value={metricsForm.total_hours_spent}
                            onChange={(e) => setMetricsForm({...metricsForm, total_hours_spent: parseInt(e.target.value) || 0})}
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Responses Received</Label>
                          <Input
                            type="number"
                            value={metricsForm.responses_received}
                            onChange={(e) => setMetricsForm({...metricsForm, responses_received: parseInt(e.target.value) || 0})}
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Positive Replies</Label>
                          <Input
                            type="number"
                            value={metricsForm.positive_replies}
                            onChange={(e) => setMetricsForm({...metricsForm, positive_replies: parseInt(e.target.value) || 0})}
                            className="bg-white/10 border-white/20 text-white"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-300">Tools Used</Label>
                        <Input
                          type="text"
                          value={metricsForm.tools_used}
                          onChange={(e) => setMetricsForm({...metricsForm, tools_used: e.target.value})}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="e.g., LinkedIn Sales Navigator, Apollo, HubSpot"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Market Focused</Label>
                        <Input
                          type="text"
                          value={metricsForm.market_focused}
                          onChange={(e) => setMetricsForm({...metricsForm, market_focused: e.target.value})}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="e.g., B2B SaaS, Enterprise Software"
                        />
                      </div>
                      <Button 
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-green-500 hover:bg-green-600"
                      >
                        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Award className="mr-2 h-4 w-4" />}
                        Submit & Get Certificate
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Certificate Status Section - After Metrics Submitted */}
            {dashboard?.certificate_status && dashboard.decision_path === 'internship' && (
              <Card className={`mt-4 ${
                dashboard.certificate_status === 'pending_generation' ? 'bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-amber-500/30' :
                dashboard.certificate_status === 'generated' ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30' :
                dashboard.certificate_status === 'downloaded' ? 'bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30' :
                'bg-slate-500/10 border-slate-500/30'
              }`}>
                <CardContent className="pt-6 text-center">
                  <Trophy className="h-16 w-16 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">Internship Outreach Complete!</h3>
                  <p className="text-slate-300 mb-6">Your metrics have been submitted. Download your certificate to proceed.</p>
                  
                  {/* Download Button - Blurred until admin uploads */}
                  <div className="relative">
                    {dashboard.certificate_status === 'pending_generation' && (
                      <>
                        <div className="blur-sm pointer-events-none">
                          <Button className="bg-green-500 hover:bg-green-600 w-full max-w-md">
                            <Download className="mr-2 h-5 w-5" />
                            Download Certificate
                          </Button>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Badge className="bg-amber-500 text-white px-4 py-2">
                            <Clock className="mr-2 h-4 w-4 inline" />
                            Waiting for Admin to Upload Certificate
                          </Badge>
                        </div>
                      </>
                    )}
                    
                    {dashboard.certificate_status === 'generated' && (
                      <div className="space-y-4">
                        <Button 
                          onClick={downloadCertificateFromAdmin}
                          className="bg-green-500 hover:bg-green-600 w-full max-w-md"
                        >
                          <Download className="mr-2 h-5 w-5" />
                          Download Certificate
                        </Button>
                        <p className="text-slate-400 text-sm">Download your certificate to proceed to the next level</p>
                      </div>
                    )}
                    
                    {dashboard.certificate_status === 'downloaded' && (
                      <div className="space-y-4">
                        <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                        <p className="text-green-400 font-semibold">Certificate Downloaded!</p>
                        {dashboard.linkedin_post_completed ? (
                          <Badge className="bg-green-500">Ready for Phase 2</Badge>
                        ) : (
                          <p className="text-slate-300">Complete the LinkedIn posting step below to proceed</p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* LinkedIn Posting Step - After Certificate Downloaded, Before Phase 2 */}
            {dashboard?.certificate_status === 'downloaded' && dashboard.decision_path === 'internship' && !dashboard?.linkedin_post_completed && (
              <Card className="mt-4 bg-gradient-to-br from-blue-600/10 to-cyan-500/10 border-blue-500/30" data-testid="linkedin-post-step">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Linkedin className="h-5 w-5 text-blue-400" />
                    Share Your Internship Achievement on LinkedIn
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <p className="text-green-400 font-medium">Congratulations on completing your internship!</p>
                  <p className="text-slate-300 text-sm">Before you move to Phase 2, take a moment to showcase your achievement publicly.</p>
                  
                  <div className="space-y-3">
                    <h4 className="text-white font-semibold text-sm">What You Need To Do:</h4>
                    <ol className="list-decimal list-inside text-slate-300 text-sm space-y-1.5 pl-1">
                      <li>Download your internship certificate (done!)</li>
                      <li>Create a LinkedIn post with your certificate image</li>
                      <li>Use the caption template below (you can personalize it)</li>
                      <li>Tag the relevant people and page</li>
                      <li>Add hashtags</li>
                      <li>Post it on LinkedIn</li>
                      <li>Add this internship under the <strong className="text-white">"Licenses & Certifications"</strong> section of your LinkedIn profile</li>
                    </ol>
                  </div>

                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <h4 className="text-white font-semibold text-sm mb-2">Suggested LinkedIn Caption:</h4>
                    <p className="text-slate-300 text-sm whitespace-pre-line leading-relaxed">
{`Just completed my internship in B2B SaaS Sales

Grateful for the hands-on learning experience around GTM strategy, outbound execution, and real-world sales workflows.

A big thank you to the team at HireForSkillz for the guidance and support throughout the journey!

Looking forward to applying these skills and growing further in sales

#B2BSales #SaaS #SalesInternship #GTM #OutboundSales #HireForSkillz #LearningInPublic`}
                    </p>
                  </div>

                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <h4 className="text-white font-semibold text-sm mb-2">Tagging Instructions:</h4>
                    <ul className="text-slate-300 text-sm space-y-1">
                      <li>Tag <strong className="text-blue-400">HireForSkillz</strong> LinkedIn Page</li>
                      <li>Tag <strong className="text-blue-400">Ravi Kumar Pandey</strong></li>
                      <li>Tag <strong className="text-blue-400">Aravindhaa</strong></li>
                    </ul>
                  </div>

                  <div className="space-y-3 pt-2">
                    <h4 className="text-white font-semibold text-sm">Confirm Completion:</h4>
                    <label className="flex items-center gap-3 cursor-pointer group" data-testid="linkedin-check-posted">
                      <input 
                        type="checkbox" 
                        checked={linkedinPostChecks.posted}
                        onChange={(e) => setLinkedinPostChecks(prev => ({ ...prev, posted: e.target.checked }))}
                        className="w-4 h-4 rounded border-slate-600 text-blue-500 focus:ring-blue-500"
                      />
                      <span className="text-slate-300 text-sm group-hover:text-white transition-colors">LinkedIn post published</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer group" data-testid="linkedin-check-cert-added">
                      <input 
                        type="checkbox" 
                        checked={linkedinPostChecks.certAdded}
                        onChange={(e) => setLinkedinPostChecks(prev => ({ ...prev, certAdded: e.target.checked }))}
                        className="w-4 h-4 rounded border-slate-600 text-blue-500 focus:ring-blue-500"
                      />
                      <span className="text-slate-300 text-sm group-hover:text-white transition-colors">Certificate added to LinkedIn "Licenses & Certifications" section</span>
                    </label>
                  </div>

                  <Button
                    onClick={submitLinkedinPostCompletion}
                    disabled={!linkedinPostChecks.posted || !linkedinPostChecks.certAdded || linkedinPostSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="linkedin-post-complete-btn"
                  >
                    {linkedinPostSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    I've Completed This Step
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* LinkedIn Post Completed Badge */}
            {dashboard?.linkedin_post_completed && dashboard?.certificate_status === 'downloaded' && dashboard.decision_path === 'internship' && (
              <Card className="mt-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/20">
                <CardContent className="pt-4 pb-4 flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-blue-400" />
                  <span className="text-blue-300 font-medium text-sm">LinkedIn Achievement Post Completed</span>
                  <Badge className="bg-blue-500/20 text-blue-300 ml-auto">Done</Badge>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* 10-Day Outreach Section for DIRECT OUTREACH path - DEPRECATED: Now unified into Phase 2 */}
        {false && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Send className="h-5 w-5 text-orange-500" />
              10-Day Direct Outreach Sequence
              {outreachData?.outreach?.is_active && !outreachData?.is_completed && (
                <Badge className="bg-orange-500 ml-2">IN PROGRESS</Badge>
              )}
              {outreachData?.is_completed && (
                <Badge className="bg-green-500 ml-2">COMPLETED</Badge>
              )}
            </h2>
            
            {/* Start Outreach Button */}
            {!outreachData?.outreach?.started_at && (
              <Card className="bg-slate-800 border-orange-500/30 mb-4">
                <CardContent className="pt-6">
                  <div className="text-center py-4">
                    <Play className="h-10 w-10 text-orange-500 mx-auto mb-3" />
                    <p className="text-slate-300 mb-4">Ready to start your 10-day outreach sequence?</p>
                    <Button 
                      onClick={startOutreach} 
                      disabled={submitting}
                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                    >
                      {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                      Start Outreach Sequence
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Important Rules Banner */}
            {outreachData?.outreach?.started_at && (
              <>
                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-amber-500/20 p-2 rounded-lg flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-amber-300 font-semibold mb-1">IMPORTANT OUTREACH RULES (NON-NEGOTIABLE)</h4>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="text-slate-300">• No Sundays</span>
                        <span className="text-slate-300">• No Holidays</span>
                        <span className="text-slate-300">• <span className="text-amber-300">9 AM - 8 PM</span> only</span>
                        <span className="text-slate-300">• Maintain discipline</span>
                      </div>
                      <p className="text-slate-500 text-xs mt-2">
                        This outreach is performance-driven, discipline-driven, and execution-driven.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Outreach Days Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.entries(OUTREACH_SEQUENCE).map(([dayNum, day]) => {
                    const isCompleted = outreachData?.outreach?.completed_days?.includes(parseInt(dayNum));
                    const isActive = parseInt(dayNum) === (outreachData?.outreach?.current_day || 1);
                    const isLocked = parseInt(dayNum) > (outreachData?.outreach?.current_day || 1);
                    
                    return (
                      <Card 
                        key={dayNum}
                        className={`cursor-pointer transition-all ${
                          isCompleted ? 'bg-green-500/20 border-green-500/50' :
                          isActive ? 'bg-orange-500/20 border-orange-500 ring-2 ring-orange-500/50' :
                          isLocked ? 'bg-slate-800/50 border-white/5 opacity-50' :
                          'bg-slate-800 border-white/10 hover:border-white/30'
                        }`}
                        onClick={() => !isLocked && setActiveStep(`outreach_${dayNum}`)}
                      >
                        <CardContent className="p-3 text-center">
                          <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center ${
                            isCompleted ? 'bg-green-500' : isActive ? day.color : 'bg-slate-700'
                          }`}>
                            {isCompleted ? <CheckCircle className="h-5 w-5 text-white" /> : 
                             isLocked ? <Lock className="h-4 w-4 text-slate-500" /> :
                             day.icon}
                          </div>
                          <p className="font-semibold text-white text-sm">Day {dayNum}</p>
                          <p className={`text-xs ${isCompleted ? 'text-green-400' : isActive ? 'text-white' : 'text-slate-400'}`}>
                            {day.action}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}

            {/* Active Day Submission Form with Instructions */}
            {outreachData?.outreach?.started_at && typeof activeStep === 'string' && activeStep.startsWith('outreach_') && (
              <Card className="mt-4 bg-slate-800 border-orange-500/30">
                {(() => {
                  const dayNum = parseInt(activeStep.split('_')[1]);
                  const dayData = OUTREACH_SEQUENCE[dayNum];
                  return (
                    <>
                      <CardHeader className="pb-4">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-white flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${dayData.color}`}>
                              {dayData.icon}
                            </div>
                            Day {dayNum}: {dayData.action}
                          </CardTitle>
                          {dayData.isRestDay && (
                            <Badge className="bg-slate-600 text-slate-200">Rest Day</Badge>
                          )}
                          {dayData.isFinal && (
                            <Badge className="bg-red-500 text-white">Final Day</Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-6">
                          {/* Today's Instructions Section */}
                          <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl p-5 border border-purple-500/30">
                            <h4 className="text-lg font-semibold text-purple-300 mb-4 flex items-center gap-2">
                              <FileText className="h-5 w-5" />
                              Today's Instructions
                            </h4>
                            
                            {/* Doables */}
                            <div className="space-y-3">
                              <p className="text-sm font-medium text-slate-300 uppercase tracking-wide">What to do:</p>
                              <div className="space-y-2">
                                {dayData.doables?.map((doable, idx) => (
                                  <div key={idx} className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
                                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/30 text-purple-300 flex items-center justify-center text-sm font-bold">
                                      {idx + 1}
                                    </span>
                                    <span className="text-slate-200">{doable}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Tips */}
                            {dayData.tips && dayData.tips.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-purple-500/20">
                                <p className="text-sm font-medium text-amber-300 mb-2 flex items-center gap-2">
                                  <Zap className="h-4 w-4" />
                                  Pro Tips:
                                </p>
                                <ul className="space-y-1">
                                  {dayData.tips.map((tip, idx) => (
                                    <li key={idx} className="text-slate-400 text-sm flex items-start gap-2">
                                      <span className="text-amber-400">•</span>
                                      {tip}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Agent Link */}
                            {dayData.agentLink && (
                              <div className="mt-4 pt-4 border-t border-purple-500/20">
                                <a 
                                  href={dayData.agentLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-4 py-2 rounded-lg font-medium transition-all"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                  Open ChatGPT Agent for This Day
                                </a>
                                <p className="text-slate-500 text-xs mt-2">
                                  Use this AI agent to help you craft your messages and scripts
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {/* Submission Form */}
                          <div className="pt-4 border-t border-white/10">
                            <h5 className="text-white font-semibold mb-4">Mark Day as Complete</h5>
                            <div className="space-y-4">
                              <div>
                                <Label className="text-slate-300">What did you accomplish? *</Label>
                                <Textarea
                                  value={outreachDayForm.notes}
                                  onChange={(e) => setOutreachDayForm({...outreachDayForm, notes: e.target.value})}
                                  className="bg-white/10 border-white/20 text-white"
                                  placeholder="Describe what you did today..."
                                  rows={3}
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-slate-300">Screenshot Link (Optional)</Label>
                                  <Input
                                    type="url"
                                    value={outreachDayForm.screenshot_link}
                                    onChange={(e) => setOutreachDayForm({...outreachDayForm, screenshot_link: e.target.value})}
                                    className="bg-white/10 border-white/20 text-white"
                                    placeholder="https://..."
                                  />
                                </div>
                                <div>
                                  <Label className="text-slate-300">Mail Trail Link (Optional)</Label>
                                  <Input
                                    type="url"
                                    value={outreachDayForm.mail_trail_link}
                                    onChange={(e) => setOutreachDayForm({...outreachDayForm, mail_trail_link: e.target.value})}
                                    className="bg-white/10 border-white/20 text-white"
                                    placeholder="https://..."
                                  />
                                </div>
                              </div>
                              <Button 
                                onClick={() => submitOutreachDay(dayNum)}
                                disabled={submitting || !outreachDayForm.notes.trim()}
                                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 h-12 text-lg"
                              >
                                {submitting ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CheckCircle className="mr-2 h-5 w-5" />}
                                Complete Day {dayNum}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </>
                  );
                })()}
              </Card>
            )}

            {/* Direct Outreach Completed */}
            {outreachData?.is_completed && (
              <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
                <CardContent className="pt-6 text-center">
                  <Trophy className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">Direct Outreach Complete!</h3>
                  <p className="text-slate-300 mb-4">Congratulations! You've completed all 10 days of outreach.</p>
                  <p className="text-slate-400 text-sm">You may now explore the Internship path if you haven't already, or continue building your network.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* 10-Day Direct Outreach REMOVED - Direct Outreach now goes to Phase 2 directly */}

        {/* Phase 2: 14-Day Outreach Program - Shows for both paths */}
        {((dashboard?.decision_path === 'internship' && outreachData?.is_completed && dashboard?.linkedin_post_completed) ||
          dashboard?.decision_path === 'direct_outreach') && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-500" />
              Phase 2: 14-Day Outreach Program
              {megamindData?.megamind?.is_active && (
                <Badge className="bg-purple-500 ml-2">IN PROGRESS</Badge>
              )}
              {megamindData?.is_completed && (
                <Badge className="bg-green-500 ml-2">COMPLETED</Badge>
              )}
            </h2>

            {/* Account Locked Warning */}
            {megamindData?.is_locked && (
              <Card className="mb-4 bg-red-500/20 border-red-500/50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <Lock className="h-8 w-8 text-red-500" />
                    <div>
                      <p className="text-red-400 font-bold text-lg">Account Blocked</p>
                      <p className="text-red-300">{megamindData?.lock_reason || 'Your account has been blocked due to missed attendance.'}</p>
                      <p className="text-slate-400 text-sm mt-1">Contact admin to unlock your account.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attendance Warning */}
            {megamindData?.attendance?.warning && !megamindData?.is_locked && (
              <Card className="mb-4 bg-amber-500/20 border-amber-500/50 animate-pulse">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-8 w-8 text-amber-500" />
                    <div>
                      <p className="text-amber-400 font-bold text-lg">⚠️ Attendance Warning!</p>
                      <p className="text-amber-300">{megamindData.attendance.message}</p>
                      <p className="text-slate-400 text-sm mt-1">Complete today's task to avoid account blocking.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Escalation Warning */}
            {megamindData?.escalation_status && megamindData.escalation_status !== 'none' && (
              <Card className="mb-4 bg-red-500/10 border-red-500/30">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-6 w-6 text-red-500" />
                    <div>
                      <p className="text-red-400 font-semibold">Escalation: {megamindData.escalation_status.replace('_', ' ').toUpperCase()}</p>
                      <p className="text-slate-400 text-sm">Level {megamindData.escalation_level}/4</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Phase 1: Megamind Class - Show first before eligibility */}
            {!megamindData?.megamind?.megamind_class_completed && (
              <Card className="bg-slate-800 border-purple-500/30 mb-4">
                <CardHeader>
                  <CardTitle className="text-white">Phase 1: Megamind Class Completion</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-400 mb-4">Confirm that you have completed the Megamind class before proceeding.</p>
                  <Button onClick={completeMegamindClass} disabled={submitting} className="bg-purple-500 hover:bg-purple-600">
                    {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                    I Have Completed Megamind Class
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Phase 2: Eligibility - Show form after class completion (not rejected) */}
            {megamindData?.is_class_completed && !megamindData?.megamind?.eligibility_submitted && megamindData?.megamind?.eligibility?.status !== 'reject' && (
              <Card className="bg-slate-800 border-purple-500/30 mb-4">
                <CardHeader>
                  <CardTitle className="text-white">Phase 2: Eligibility Validation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-400 mb-4">Provide the following links to become eligible for the 14-Day Outreach Program:</p>
                  <form onSubmit={submitMegamindEligibility} className="space-y-4">
                    {/* Portfolio Link */}
                    <div>
                      <Label className="text-slate-300">Portfolio Link (Ask for your portfolio if you have not received it yet)</Label>
                      <Input
                        type="url"
                        required
                        value={megamindEligibilityForm.portfolio_link}
                        onChange={(e) => setMegamindEligibilityForm({...megamindEligibilityForm, portfolio_link: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="https://..."
                        data-testid="portfolio-link-input"
                      />
                    </div>
                    
                    {/* Link Input Fields */}
                    <div className="grid md:grid-cols-3 gap-4">
                      {/* Company Sheet Link */}
                      <div>
                        <Label className="text-slate-300 mb-2 block">Company Sheet Link</Label>
                        <Input
                          type="url"
                          required
                          value={megamindEligibilityForm.companies_list_link}
                          onChange={(e) => setMegamindEligibilityForm({...megamindEligibilityForm, companies_list_link: e.target.value})}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="https://docs.google.com/..."
                          data-testid="companies-list-link"
                        />
                      </div>
                      
                      {/* People Sheet Link */}
                      <div>
                        <Label className="text-slate-300 mb-2 block">People Sheet Link</Label>
                        <Input
                          type="url"
                          required
                          value={megamindEligibilityForm.prospect_list_link}
                          onChange={(e) => setMegamindEligibilityForm({...megamindEligibilityForm, prospect_list_link: e.target.value})}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="https://docs.google.com/..."
                          data-testid="prospect-list-link"
                        />
                      </div>
                      
                      {/* Market Research Link */}
                      <div>
                        <Label className="text-slate-300 mb-2 block">Market Research Link</Label>
                        <Input
                          type="url"
                          required
                          value={megamindEligibilityForm.market_research_link}
                          onChange={(e) => setMegamindEligibilityForm({...megamindEligibilityForm, market_research_link: e.target.value})}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="https://docs.google.com/..."
                          data-testid="market-research-link"
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      disabled={submitting || !megamindEligibilityForm.companies_list_link || !megamindEligibilityForm.prospect_list_link || !megamindEligibilityForm.market_research_link} 
                      className="bg-purple-500 hover:bg-purple-600 w-full"
                      data-testid="submit-eligibility-btn"
                    >
                      {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
                      Submit Eligibility Links
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Rejected Eligibility - Show Re-upload Form (only after class is completed) */}
            {megamindData?.is_class_completed && megamindData?.megamind?.eligibility?.status === 'reject' && (
              <Card className="bg-red-500/10 border-red-500/30 mb-4" data-testid="megamind-rejection-card">
                <CardContent className="pt-6">
                  <div className="text-center mb-4">
                    <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
                    <p className="text-red-400 font-semibold text-lg">Documents Rejected</p>
                  </div>
                  
                  {/* Show rejection reason */}
                  <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-300 font-medium mb-1">Rejection Reason:</p>
                    <p className="text-white">{megamindData?.megamind?.eligibility?.rejection_reason || megamindData?.megamind?.eligibility?.admin_review?.comments || 'No reason provided'}</p>
                  </div>
                  
                  <p className="text-slate-400 text-sm mb-4 text-center">Please correct the issues and re-upload your documents below.</p>
                  
                  {/* Re-upload Form - Same fields as original eligibility form */}
                  <form onSubmit={submitMegamindEligibility} className="space-y-4">
                    {/* Portfolio Link */}
                    <div>
                      <Label className="text-slate-300">Portfolio Link</Label>
                      <Input
                        type="url"
                        required
                        value={megamindEligibilityForm.portfolio_link}
                        onChange={(e) => setMegamindEligibilityForm({...megamindEligibilityForm, portfolio_link: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="https://..."
                        data-testid="resubmit-portfolio-link-input"
                      />
                    </div>
                    
                    {/* File Upload Fields */}
                    <div className="grid md:grid-cols-3 gap-4">
                      {/* 30 Companies List */}
                      <div>
                        <Label className="text-slate-300 mb-2 block">30 Companies List</Label>
                        <p className="text-slate-500 text-xs mb-2">Excel, PDF, or Doc (max 10MB)</p>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".xlsx,.xls,.csv,.pdf,.doc,.docx"
                            onChange={(e) => handleFileUpload(e, 'companies_list')}
                            className="hidden"
                            id="resubmit_companies_list_upload"
                            data-testid="resubmit-companies-list-upload"
                          />
                          <label
                            htmlFor="resubmit_companies_list_upload"
                            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-md cursor-pointer transition-colors ${
                              uploadedFiles.companies_list 
                                ? 'bg-green-500/20 border border-green-500/50 text-green-400' 
                                : 'bg-white/10 border border-white/20 text-slate-300 hover:bg-white/20'
                            }`}
                          >
                            {uploadingFile === 'companies_list' ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : uploadedFiles.companies_list ? (
                              <>
                                <CheckCircle className="h-4 w-4" />
                                <span className="text-sm truncate max-w-[120px]">{uploadedFiles.companies_list.original_filename}</span>
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4" />
                                <span className="text-sm">Choose File</span>
                              </>
                            )}
                          </label>
                        </div>
                      </div>
                      
                      {/* Prospect List */}
                      <div>
                        <Label className="text-slate-300 mb-2 block">Prospect List</Label>
                        <p className="text-slate-500 text-xs mb-2">Excel, PDF, or Doc (max 10MB)</p>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".xlsx,.xls,.csv,.pdf,.doc,.docx"
                            onChange={(e) => handleFileUpload(e, 'prospect_list')}
                            className="hidden"
                            id="resubmit_prospect_list_upload"
                            data-testid="resubmit-prospect-list-upload"
                          />
                          <label
                            htmlFor="resubmit_prospect_list_upload"
                            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-md cursor-pointer transition-colors ${
                              uploadedFiles.prospect_list 
                                ? 'bg-green-500/20 border border-green-500/50 text-green-400' 
                                : 'bg-white/10 border border-white/20 text-slate-300 hover:bg-white/20'
                            }`}
                          >
                            {uploadingFile === 'prospect_list' ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : uploadedFiles.prospect_list ? (
                              <>
                                <CheckCircle className="h-4 w-4" />
                                <span className="text-sm truncate max-w-[120px]">{uploadedFiles.prospect_list.original_filename}</span>
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4" />
                                <span className="text-sm">Choose File</span>
                              </>
                            )}
                          </label>
                        </div>
                      </div>
                      
                      {/* Messaging Doc */}
                      <div>
                        <Label className="text-slate-300 mb-2 block">Messaging Doc (5+5 Followups)</Label>
                        <p className="text-slate-500 text-xs mb-2">Excel, PDF, or Doc (max 10MB)</p>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".xlsx,.xls,.csv,.pdf,.doc,.docx"
                            onChange={(e) => handleFileUpload(e, 'messaging_doc')}
                            className="hidden"
                            id="resubmit_messaging_doc_upload"
                            data-testid="resubmit-messaging-doc-upload"
                          />
                          <label
                            htmlFor="resubmit_messaging_doc_upload"
                            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-md cursor-pointer transition-colors ${
                              uploadedFiles.messaging_doc 
                                ? 'bg-green-500/20 border border-green-500/50 text-green-400' 
                                : 'bg-white/10 border border-white/20 text-slate-300 hover:bg-white/20'
                            }`}
                          >
                            {uploadingFile === 'messaging_doc' ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : uploadedFiles.messaging_doc ? (
                              <>
                                <CheckCircle className="h-4 w-4" />
                                <span className="text-sm truncate max-w-[120px]">{uploadedFiles.messaging_doc.original_filename}</span>
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4" />
                                <span className="text-sm">Choose File</span>
                              </>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      disabled={submitting || !uploadedFiles.companies_list || !uploadedFiles.prospect_list || !uploadedFiles.messaging_doc} 
                      className="w-full bg-amber-500 hover:bg-amber-600" 
                      data-testid="resubmit-eligibility-btn"
                    >
                      {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                      Re-submit Eligibility Documents
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {megamindData?.is_class_completed && megamindData?.megamind?.eligibility_submitted && !megamindData?.megamind?.eligibility_approved && megamindData?.megamind?.eligibility?.status !== 'reject' && (
              <Card className="bg-amber-500/10 border-amber-500/30 mb-4">
                <CardContent className="pt-6 text-center">
                  <Clock className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                  <p className="text-amber-400 font-semibold">Eligibility Under Review</p>
                  <p className="text-slate-400 text-sm">Admin will review your documents and approve access.</p>
                </CardContent>
              </Card>
            )}

            {/* Post-Eligibility Flow: 3 Steps before 14-Day Outreach */}
            {megamindData?.is_ready && (() => {
              const p2 = megamindData?.phase2_flow || {};
              const p2Outreach = megamindData?.phase2_outreach;
              const whatsapp = p2?.whatsapp || {};
              const jobProfile = p2?.job_profile || {};
              const currentStep = p2?.current_step || 1;

              // If outreach already started, don't show the 3-step flow
              if (p2Outreach?.started_at) return null;

              return (
                <div className="space-y-3 mb-4">
                  {/* Step 1: Join Outreach WhatsApp Group */}
                  <Card className={`border-l-4 ${whatsapp.completed ? 'border-l-green-500 bg-green-500/5 border-green-500/20' : currentStep === 1 ? 'border-l-amber-500 bg-slate-800 border-amber-500/30' : 'bg-slate-800/50 border-slate-700 opacity-50'}`} data-testid="phase2-whatsapp-step">
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {whatsapp.completed ? <CheckCircle className="h-5 w-5 text-green-500" /> : <MessageSquare className="h-5 w-5 text-amber-400" />}
                          <div>
                            <h4 className="text-white font-medium">Step 1: Join Outreach WhatsApp Group</h4>
                            {whatsapp.completed && <span className="text-green-400 text-xs">Completed</span>}
                          </div>
                        </div>
                        {currentStep === 1 && !whatsapp.requested && (
                          <Button onClick={phase2WhatsappRequest} disabled={submitting} size="sm" className="bg-amber-500 hover:bg-amber-600" data-testid="phase2-whatsapp-request-btn">
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                            Request to Join
                          </Button>
                        )}
                      </div>
                      {currentStep === 1 && !whatsapp.requested && (
                        <p className="text-slate-400 text-sm mt-2 ml-8">Before starting your outreach journey, join the official outreach WhatsApp group for updates and support.</p>
                      )}
                      {whatsapp.requested && !whatsapp.completed && (
                        <div className="mt-3 ml-8 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                          <p className="text-amber-400 text-sm flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Request sent! Waiting for admin to share the WhatsApp group link...
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Step 2: Create Job Profiles */}
                  <Card className={`border-l-4 ${jobProfile.confirmed ? 'border-l-green-500 bg-green-500/5 border-green-500/20' : currentStep === 2 ? 'border-l-blue-500 bg-slate-800 border-blue-500/30' : 'bg-slate-800/50 border-slate-700 opacity-50'}`} data-testid="phase2-job-profile-step">
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {jobProfile.confirmed ? <CheckCircle className="h-5 w-5 text-green-500" /> : <Briefcase className="h-5 w-5 text-blue-400" />}
                          <div>
                            <h4 className="text-white font-medium">Step 2: Create Job Profiles</h4>
                            {jobProfile.confirmed && <span className="text-green-400 text-xs">Completed</span>}
                          </div>
                        </div>
                        {currentStep === 2 && !jobProfile.requested && (
                          <Button onClick={phase2JobProfileRequest} disabled={submitting} size="sm" className="bg-blue-500 hover:bg-blue-600" data-testid="phase2-job-profile-request-btn">
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                            Request Job Profile Mandate Document
                          </Button>
                        )}
                      </div>
                      {currentStep === 2 && !jobProfile.requested && (
                        <p className="text-slate-400 text-sm mt-2 ml-8">Before starting outreach, create optimized job profiles across relevant platforms. We'll provide a step-by-step guide.</p>
                      )}
                      {jobProfile.requested && !jobProfile.doc_link && (
                        <div className="mt-3 ml-8 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                          <p className="text-blue-400 text-sm flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Request sent! Waiting for admin to share the Job Profile Mandate Document...
                          </p>
                        </div>
                      )}
                      {jobProfile.doc_link && !jobProfile.confirmed && (
                        <div className="mt-3 ml-8 space-y-3">
                          <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                            <p className="text-green-400 text-sm font-medium mb-2">Your guide is ready!</p>
                            <a href={jobProfile.doc_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-blue-400 hover:underline text-sm" data-testid="phase2-download-doc-btn">
                              <Download className="h-4 w-4" /> Download the List
                            </a>
                          </div>
                          <Button onClick={phase2JobProfileConfirm} disabled={submitting} size="sm" className="bg-green-600 hover:bg-green-700" data-testid="phase2-job-profile-confirm-btn">
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                            Confirm & Move Next
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Step 3: Start Outreach */}
                  <Card className={`border-l-4 ${currentStep >= 3 ? 'border-l-purple-500 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/30' : 'bg-slate-800/50 border-slate-700 opacity-50'}`} data-testid="phase2-start-outreach-step">
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-center gap-3">
                        <Play className="h-5 w-5 text-purple-400" />
                        <h4 className="text-white font-medium">Step 3: Start 14-Day Outreach</h4>
                      </div>
                      {currentStep >= 3 && (
                        <div className="mt-3 ml-8 space-y-3">
                          <p className="text-slate-300 text-sm">You've joined the outreach group and set up your job profiles. Now it's time to begin your outreach journey!</p>
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                            <p className="text-amber-300 text-sm font-medium flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                              Always start Outreach on a Monday for best results and proper tracking.
                            </p>
                          </div>
                          <Button onClick={phase2StartOutreach} disabled={submitting} className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600" data-testid="phase2-start-outreach-btn">
                            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                            Start Outreach
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              );
            })()}

            {/* 14-Day Outreach - Day by Day (Phase 2 Outreach) */}
            {megamindData?.phase2_outreach?.started_at && (
              <div className="space-y-3">
                <h3 className="text-white font-semibold text-sm mb-2">14-Day Outreach Progress ({megamindData.phase2_outreach.completed_days?.length || 0}/14 Days)</h3>
                {Object.entries(megamindData?.phase2_outreach_sequence || {}).map(([dayNum, dayInfo]) => {
                  const num = parseInt(dayNum);
                  const currentDay = megamindData.phase2_outreach?.current_day || 1;
                  const completedDays = megamindData.phase2_outreach?.completed_days || [];
                  const isCompleted = completedDays.includes(num);
                  const isActive = num === currentDay && !isCompleted;
                  const isLocked = num > currentDay;
                  const isRestDay = dayInfo.action?.includes('REST');

                  return (
                    <Card 
                      key={num}
                      className={`border-l-4 transition-all ${
                        isCompleted ? 'border-l-green-500 bg-green-500/5 border-green-500/20' :
                        isActive ? 'border-l-purple-500 bg-slate-800 border-purple-500/30' :
                        'border-l-slate-600 bg-slate-800/50 border-slate-700 opacity-50'
                      }`}
                      data-testid={`phase2-outreach-day-${num}`}
                    >
                      <CardContent className="pt-4 pb-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-3">
                            <span className={`text-xs font-bold w-8 h-8 rounded-full flex items-center justify-center ${
                              isCompleted ? 'bg-green-500/20 text-green-400' :
                              isActive ? 'bg-purple-500/20 text-purple-400' :
                              'bg-slate-700 text-slate-500'
                            }`}>
                              {num}
                            </span>
                            <div>
                              <h4 className={`text-sm font-medium ${isCompleted ? 'text-green-400' : isActive ? 'text-white' : 'text-slate-500'}`}>
                                {dayInfo.action}
                              </h4>
                              {isCompleted && <span className="text-green-400 text-[10px]">Completed</span>}
                            </div>
                          </div>
                          {isCompleted && <CheckCircle className="h-4 w-4 text-green-500" />}
                          {isLocked && <Lock className="h-4 w-4 text-slate-500" />}
                        </div>

                        {/* Active Day - Show details and submission form */}
                        {isActive && (
                          <div className="mt-3 ml-11 space-y-3">
                            <p className="text-slate-400 text-sm">{dayInfo.description}</p>
                            
                            {/* Conditional step warning for Day 3 and Day 5 */}
                            {dayInfo.conditional && (
                              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5">
                                <p className="text-amber-400 text-xs flex items-center gap-2">
                                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                                  {dayInfo.conditional}
                                </p>
                              </div>
                            )}

                            {/* Doables */}
                            {dayInfo.doables && (
                              <div className="bg-white/5 rounded-lg p-3">
                                <p className="text-xs text-slate-500 mb-2 font-medium">To Do:</p>
                                <ul className="space-y-1">
                                  {dayInfo.doables.map((item, i) => (
                                    <li key={i} className="text-xs text-slate-300 flex items-start gap-2">
                                      <ChevronRight className="h-3 w-3 text-purple-400 mt-0.5 flex-shrink-0" />
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Submission Form */}
                            {isRestDay ? (
                              <div className="space-y-3">
                                <div>
                                  <Label className="text-slate-300 text-xs">Notes (Optional)</Label>
                                  <Textarea
                                    value={megamindDayForm.notes}
                                    onChange={(e) => setMegamindDayForm({...megamindDayForm, notes: e.target.value})}
                                    className="bg-white/10 border-white/20 text-white text-sm"
                                    placeholder="What did you review today?"
                                    rows={2}
                                  />
                                </div>
                                <Button
                                  onClick={() => submitPhase2OutreachDay(num)}
                                  disabled={submitting}
                                  size="sm"
                                  className="bg-slate-600 hover:bg-slate-500"
                                  data-testid={`phase2-submit-day-${num}`}
                                >
                                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                                  Mark Day {num} Complete
                                </Button>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <div>
                                  <Label className="text-slate-300 text-xs">Notes (What you did) *</Label>
                                  <Textarea
                                    value={megamindDayForm.notes}
                                    onChange={(e) => setMegamindDayForm({...megamindDayForm, notes: e.target.value})}
                                    className="bg-white/10 border-white/20 text-white text-sm"
                                    placeholder="Describe your outreach activities..."
                                    rows={2}
                                    required
                                  />
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                  <div>
                                    <Label className="text-slate-300 text-xs">Screenshot Link</Label>
                                    <Input
                                      type="url"
                                      value={megamindDayForm.screenshot_link}
                                      onChange={(e) => setMegamindDayForm({...megamindDayForm, screenshot_link: e.target.value})}
                                      className="bg-white/10 border-white/20 text-white text-sm"
                                      placeholder="https://..."
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-slate-300 text-xs">Mail Trail Link</Label>
                                    <Input
                                      type="url"
                                      value={megamindDayForm.mail_trail_link}
                                      onChange={(e) => setMegamindDayForm({...megamindDayForm, mail_trail_link: e.target.value})}
                                      className="bg-white/10 border-white/20 text-white text-sm"
                                      placeholder="https://..."
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-slate-300 text-xs">LinkedIn Screenshot</Label>
                                    <Input
                                      type="url"
                                      value={megamindDayForm.linkedin_screenshot_link}
                                      onChange={(e) => setMegamindDayForm({...megamindDayForm, linkedin_screenshot_link: e.target.value})}
                                      className="bg-white/10 border-white/20 text-white text-sm"
                                      placeholder="https://..."
                                    />
                                  </div>
                                </div>
                                <Button
                                  onClick={() => submitPhase2OutreachDay(num)}
                                  disabled={submitting || !megamindDayForm.notes}
                                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                                  data-testid={`phase2-submit-day-${num}`}
                                >
                                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                                  Complete Day {num}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Megamind Completed - Repeat/Exit + Offer Letter Upload */}
            {megamindData?.is_completed && (
              <Card className="mt-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30">
                <CardContent className="pt-6">
                  <div className="text-center mb-6">
                    <Trophy className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white mb-2">Congratulations!</h3>
                    <p className="text-slate-300">You have successfully completed the 14-Day Outreach Program!</p>
                    {megamindData?.megamind?.repeat_count > 0 && (
                      <p className="text-purple-400 text-sm mt-1">Cycle {(megamindData.megamind.repeat_count || 0) + 1} completed</p>
                    )}
                  </div>
                  
                  {/* Repeat/Exit Options - Only show if not exited to interview */}
                  {!megamindData?.megamind?.exited_to_interview && !megamindData?.megamind?.offer_letter?.submitted && (
                    <div className="bg-slate-800/50 rounded-lg p-4 mb-6" data-testid="repeat-exit-options">
                      <h4 className="text-white font-semibold mb-3">Do you want to repeat and scale outreach for another 14 days?</h4>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button 
                          onClick={repeatOutreachProgram} 
                          disabled={submitting}
                          className="bg-purple-500 hover:bg-purple-600 flex-1"
                          data-testid="repeat-outreach-btn"
                        >
                          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
                          Yes, Repeat for Another 14 Days
                        </Button>
                        <Button 
                          onClick={exitToInterview} 
                          disabled={submitting}
                          variant="outline"
                          className="border-green-500/50 text-green-400 hover:bg-green-500/10 flex-1"
                          data-testid="exit-to-interview-btn"
                        >
                          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
                          No, Move to Interview Stage
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Check if offer letter already submitted */}
                  {megamindData?.megamind?.offer_letter?.submitted ? (
                    <div className="text-center">
                      <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4 mb-4">
                        <Clock className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                        <p className="text-amber-400 font-semibold">Offer Letter Submitted!</p>
                        <p className="text-slate-400 text-sm mt-1">
                          Company: {megamindData.megamind.offer_letter.company_name} | Role: {megamindData.megamind.offer_letter.role}
                        </p>
                        <p className="text-slate-500 text-sm mt-2">Admin will review and provide your certificate soon.</p>
                      </div>
                      
                      {/* Show certificate download if available */}
                      {dashboard?.certificate_url && (
                        <Button onClick={downloadCertificateFromAdmin} className="bg-green-500 hover:bg-green-600">
                          <Download className="mr-2 h-4 w-4" />
                          Download Your Certificate
                        </Button>
                      )}
                    </div>
                  ) : (
                    /* Offer Letter Upload Form */
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        <FileText className="h-5 w-5 text-amber-500" />
                        Submit Your Offer Letter
                      </h4>
                      <p className="text-slate-400 text-sm mb-4">
                        Please upload your offer letter from the company you got selected at. This is required to receive your completion certificate.
                      </p>
                      
                      <form onSubmit={submitOfferLetter} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-slate-300">Company Name *</Label>
                            <Input
                              type="text"
                              required
                              value={offerLetterForm.company_name}
                              onChange={(e) => setOfferLetterForm({...offerLetterForm, company_name: e.target.value})}
                              className="bg-white/10 border-white/20 text-white"
                              placeholder="e.g., Salesforce"
                              data-testid="offer-company-input"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300">Role/Position *</Label>
                            <Input
                              type="text"
                              required
                              value={offerLetterForm.role}
                              onChange={(e) => setOfferLetterForm({...offerLetterForm, role: e.target.value})}
                              className="bg-white/10 border-white/20 text-white"
                              placeholder="e.g., SDR, BDR, Account Executive"
                              data-testid="offer-role-input"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300">Offer Date</Label>
                            <Input
                              type="date"
                              value={offerLetterForm.offer_date}
                              onChange={(e) => setOfferLetterForm({...offerLetterForm, offer_date: e.target.value})}
                              className="bg-white/10 border-white/20 text-white"
                              data-testid="offer-date-input"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300">Offer Letter (PDF, Image, Doc) *</Label>
                            <div className="relative">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                onChange={handleOfferLetterUpload}
                                className="hidden"
                                id="offer_letter_upload"
                                data-testid="offer-letter-upload"
                              />
                              <label
                                htmlFor="offer_letter_upload"
                                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors ${
                                  offerLetterFile 
                                    ? 'bg-green-500/20 border border-green-500/50 text-green-400' 
                                    : 'bg-white/10 border border-white/20 text-slate-300 hover:bg-white/20'
                                }`}
                              >
                                {offerLetterFile ? (
                                  <>
                                    <CheckCircle className="h-4 w-4" />
                                    <span className="text-sm truncate max-w-[150px]">{offerLetterFile.name}</span>
                                  </>
                                ) : (
                                  <>
                                    <Upload className="h-4 w-4" />
                                    <span className="text-sm">Choose File</span>
                                  </>
                                )}
                              </label>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-slate-300">Additional Notes (Optional)</Label>
                          <Textarea
                            value={offerLetterForm.notes}
                            onChange={(e) => setOfferLetterForm({...offerLetterForm, notes: e.target.value})}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="Any additional details about your offer..."
                            data-testid="offer-notes-input"
                          />
                        </div>
                        
                        <Button 
                          type="submit" 
                          disabled={uploadingOfferLetter || !offerLetterFile}
                          className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                          data-testid="submit-offer-letter-btn"
                        >
                          {uploadingOfferLetter ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Award className="mr-2 h-4 w-4" />
                          )}
                          Submit Offer Letter & Request Certificate
                        </Button>
                      </form>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Certificate Modal */}
        {showCertificate && certificateData && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl bg-white">
              <CardContent className="p-8">
                <div className="text-center border-4 border-amber-500 p-8 rounded-lg">
                  <Award className="h-16 w-16 text-amber-500 mx-auto mb-4" />
                  <h2 className="text-3xl font-bold text-slate-900 mb-2">Certificate of Completion</h2>
                  <p className="text-slate-600 mb-6">This certifies that</p>
                  <h3 className="text-2xl font-bold text-amber-600 mb-2">{certificateData.student_name}</h3>
                  <p className="text-slate-600 mb-4">has successfully completed the</p>
                  <h4 className="text-xl font-semibold text-slate-800 mb-6">{certificateData.program_completed}</h4>
                  
                  <div className="grid grid-cols-2 gap-4 text-left mb-6 bg-slate-50 p-4 rounded-lg">
                    <div><span className="text-slate-500">Certificate ID:</span> <span className="font-semibold">{certificateData.certificate_id}</span></div>
                    <div><span className="text-slate-500">Issue Date:</span> <span className="font-semibold">{certificateData.issue_date}</span></div>
                    <div><span className="text-slate-500">Total Score:</span> <span className="font-bold text-amber-600">{certificateData.total_score}/100</span></div>
                    <div><span className="text-slate-500">Referral Eligible:</span> <span className={certificateData.referral_eligible ? 'text-green-600 font-bold' : 'text-slate-500'}>{certificateData.referral_eligible ? 'Yes ✓' : 'No'}</span></div>
                  </div>
                  
                  {certificateData.achievements?.length > 0 && (
                    <div className="mb-6">
                      <p className="text-slate-500 mb-2">Achievements:</p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {certificateData.achievements.map((a, i) => (
                          <Badge key={i} className="bg-amber-500">{a}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-slate-500 italic">{certificateData.signature}</p>
                </div>
                <Button onClick={() => setShowCertificate(false)} className="w-full mt-4 bg-slate-900">
                  Close
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Upcoming Interviews Section */}
        <div className="mt-8" id="upcoming-interviews-section" data-testid="upcoming-interviews-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-500" />
              Upcoming Interviews
            </h2>
            <Button 
              onClick={() => setShowInterviewForm(!showInterviewForm)}
              className="bg-amber-500 hover:bg-amber-600"
              size="sm"
              data-testid="add-interview-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Interview
            </Button>
          </div>

          {/* Add Interview Form */}
          {showInterviewForm && (
            <Card className="bg-slate-800 border-amber-500/30 mb-4" data-testid="interview-form">
              <CardHeader>
                <CardTitle className="text-white text-base">New Interview Details</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={addInterview} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Interview Date & Time *</Label>
                      <Input
                        type="datetime-local"
                        required
                        value={interviewForm.interview_date}
                        onChange={(e) => setInterviewForm({...interviewForm, interview_date: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        data-testid="interview-date-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Company Name *</Label>
                      <Input
                        type="text"
                        required
                        value={interviewForm.company_name}
                        onChange={(e) => setInterviewForm({...interviewForm, company_name: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="e.g., Salesforce"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Role/Position *</Label>
                      <Input
                        type="text"
                        required
                        value={interviewForm.role}
                        onChange={(e) => setInterviewForm({...interviewForm, role: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="e.g., SDR, BDR, Account Executive"
                        data-testid="role-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Product (What they sell) *</Label>
                      <Input
                        type="text"
                        required
                        value={interviewForm.product}
                        onChange={(e) => setInterviewForm({...interviewForm, product: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="e.g., CRM software, Marketing automation"
                        data-testid="product-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300">Extra Notes (Optional)</Label>
                    <Textarea
                      value={interviewForm.notes}
                      onChange={(e) => setInterviewForm({...interviewForm, notes: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="Any additional details about the interview..."
                      data-testid="notes-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={submitting} className="bg-amber-500 hover:bg-amber-600">
                      {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                      Add Interview
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowInterviewForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Interview List */}
          {interviews.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-6 text-center">
                <Calendar className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No upcoming interviews yet.</p>
                <p className="text-slate-500 text-sm mt-1">Add your first interview to start preparing!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {interviews.map((interview) => (
                <Card 
                  key={interview.id} 
                  className={`
                    border transition-all
                    ${interview.status === 'mock_requested' ? 'bg-amber-500/10 border-amber-500/30' : ''}
                    ${interview.status === 'mock_completed' ? 'bg-blue-500/10 border-blue-500/30' : ''}
                    ${interview.status === 'interview_done' ? 'bg-green-500/10 border-green-500/30' : ''}
                    ${interview.status === 'scheduled' ? 'bg-slate-800 border-slate-700' : ''}
                  `}
                  data-testid={`interview-card-${interview.id}`}
                >
                  <CardContent className="pt-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="h-4 w-4 text-amber-500" />
                          <h3 className="text-white font-semibold">{interview.company_name}</h3>
                          <span className={`
                            text-xs px-2 py-0.5 rounded-full
                            ${interview.status === 'scheduled' ? 'bg-slate-600 text-slate-200' : ''}
                            ${interview.status === 'mock_requested' ? 'bg-amber-500/20 text-amber-400' : ''}
                            ${interview.status === 'mock_completed' ? 'bg-blue-500/20 text-blue-400' : ''}
                            ${interview.status === 'interview_done' ? 'bg-green-500/20 text-green-400' : ''}
                          `}>
                            {interview.status === 'scheduled' && 'Scheduled'}
                            {interview.status === 'mock_requested' && 'Mock Requested'}
                            {interview.status === 'mock_completed' && 'Mock Done'}
                            {interview.status === 'interview_done' && 'Completed'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <p className="text-slate-500">Role</p>
                            <p className="text-white">{interview.role}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Product</p>
                            <p className="text-white">{interview.product}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Interview Date</p>
                            <p className="text-white">
                              {new Date(interview.interview_date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          {interview.notes && (
                            <div className="col-span-2 md:col-span-1">
                              <p className="text-slate-500">Notes</p>
                              <p className="text-white text-xs">{interview.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {interview.status === 'scheduled' && (
                          <Button
                            onClick={() => requestMockCall(interview.id, interview.company_name)}
                            disabled={submitting}
                            className="bg-amber-500 hover:bg-amber-600"
                            size="sm"
                            data-testid={`request-mock-btn-${interview.id}`}
                          >
                            <PhoneCall className="h-4 w-4 mr-1" />
                            Request Mock Call
                          </Button>
                        )}
                        {interview.status === 'mock_requested' && (
                          <span className="text-amber-400 text-sm flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            Waiting for mock call...
                          </span>
                        )}
                        {interview.status === 'mock_completed' && (
                          <Button
                            onClick={() => updateInterviewStatus(interview.id, 'interview_done')}
                            disabled={submitting}
                            className="bg-green-500 hover:bg-green-600"
                            size="sm"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Mark Interview Done
                          </Button>
                        )}
                        {interview.status !== 'interview_done' && (
                          <Button
                            onClick={() => deleteInterview(interview.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Violations */}
        {dashboard?.violations && dashboard.violations.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Violations History
            </h2>
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="pt-4">
                {dashboard.violations.map((v, idx) => (
                  <div key={idx} className="flex items-start gap-3 py-2 border-b border-red-500/20 last:border-0">
                    <XCircle className="h-4 w-4 text-red-500 mt-1" />
                    <div>
                      <p className="text-white text-sm">{v.reason || v.type}</p>
                      <p className="text-slate-500 text-xs">{new Date(v.locked_at || v.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Leaderboard Modal */}
        {showLeaderboard && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 p-4 overflow-y-auto">
            <div className="min-h-full flex items-start justify-center py-8">
              <Card className="w-full max-w-3xl bg-slate-900 border-amber-500/30 relative">
                {/* Sticky Close Button */}
                <button
                  onClick={() => setShowLeaderboard(false)}
                  className="absolute top-4 right-4 text-slate-400 hover:text-white z-20 bg-slate-800/80 rounded-full p-2 hover:bg-slate-700 transition-colors"
                  data-testid="close-leaderboard-btn"
                >
                  <X className="h-5 w-5" />
                </button>
                <CardHeader className="border-b border-white/10 text-center pt-6">
                  <div className="mx-auto bg-gradient-to-r from-amber-500 to-orange-500 w-14 h-14 rounded-full flex items-center justify-center mb-3">
                    <Trophy className="h-7 w-7 text-white" />
                  </div>
                  <CardTitle className="text-white text-2xl">Leaderboard</CardTitle>
                  <p className="text-slate-400 text-sm">Rankings based on completion progress & attendance</p>
                </CardHeader>
              <CardContent className="p-6">
                {leaderboardLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" />
                  </div>
                ) : (
                  <>
                    {/* Current User Highlight */}
                    {leaderboard?.current_user_entry && (
                      <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4 mb-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="bg-amber-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg">
                              #{leaderboard.current_user_entry.rank}
                            </div>
                            <div>
                              <p className="text-white font-medium">Your Position</p>
                              <p className="text-amber-400 text-sm">{leaderboard.current_user_entry.badge}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-amber-400">{leaderboard.current_user_entry.combined_score}</p>
                            <p className="text-xs text-slate-400">Combined Score</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/10">
                          <div className="text-center">
                            <p className="text-white font-medium">{leaderboard.current_user_entry.completion_score}</p>
                            <p className="text-xs text-slate-400">Completion</p>
                          </div>
                          <div className="text-center">
                            <p className="text-white font-medium">{leaderboard.current_user_entry.attendance_days}</p>
                            <p className="text-xs text-slate-400">Days Attended</p>
                          </div>
                          <div className="text-center">
                            <p className="text-white font-medium">{leaderboard.current_user_entry.attendance_hours}h</p>
                            <p className="text-xs text-slate-400">Total Hours</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Leaderboard Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-slate-400 text-sm border-b border-white/10">
                            <th className="text-left p-3">Rank</th>
                            <th className="text-left p-3">Name</th>
                            <th className="text-center p-3">Badge</th>
                            <th className="text-center p-3">Score</th>
                            <th className="text-center p-3">Days</th>
                            <th className="text-center p-3">Hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leaderboard?.leaderboard?.map((entry, idx) => (
                            <tr 
                              key={entry.student_id} 
                              className={`border-b border-white/5 ${
                                entry.is_current_user ? 'bg-amber-500/10' : ''
                              } ${entry.is_online ? '' : ''}`}
                            >
                              <td className="p-3">
                                <span className={`font-bold ${
                                  idx === 0 ? 'text-amber-400' :
                                  idx === 1 ? 'text-slate-300' :
                                  idx === 2 ? 'text-orange-400' : 'text-white'
                                }`}>
                                  {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${entry.rank}`}
                                </span>
                              </td>
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-white">
                                    {entry.name}
                                    {entry.is_current_user && <span className="text-amber-400 text-xs ml-1">(You)</span>}
                                  </span>
                                  {entry.is_online && (
                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" title="Online"></span>
                                  )}
                                </div>
                              </td>
                              <td className="p-3 text-center">
                                <span className="text-sm">{entry.badge}</span>
                              </td>
                              <td className="p-3 text-center">
                                <span className={`font-bold ${
                                  entry.combined_score >= 60 ? 'text-green-400' :
                                  entry.combined_score >= 30 ? 'text-amber-400' : 'text-white'
                                }`}>{entry.combined_score}</span>
                              </td>
                              <td className="p-3 text-center text-slate-300">{entry.attendance_days}</td>
                              <td className="p-3 text-center text-slate-300">{entry.attendance_hours}h</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Legend */}
                    <div className="mt-6 p-4 bg-white/5 rounded-lg">
                      <p className="text-slate-400 text-sm mb-2 font-medium">How Scores Are Calculated:</p>
                      <div className="grid md:grid-cols-2 gap-2 text-xs text-slate-500">
                        <p>• Completion Progress (60%): Steps + Decision Path + Scores</p>
                        <p>• Attendance (40%): Days × 2 + Bonus for 8+ hour days</p>
                        <p>• 🏆 Champion: 80+ | 🥇 Gold: 60+ | 🥈 Silver: 40+</p>
                        <p>• 🥉 Bronze: 20+ | 🌱 Starter: &lt;20</p>
                      </div>
                    </div>
                    
                    {/* Close Button at Bottom */}
                    <Button
                      onClick={() => setShowLeaderboard(false)}
                      className="w-full mt-6 bg-slate-800 hover:bg-slate-700 text-white"
                      data-testid="close-leaderboard-bottom-btn"
                    >
                      Close Leaderboard
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Step Card Component
function StepCard({ 
  stepNum, 
  step, 
  currentStep, 
  isActive, 
  onToggle,
  onSubmitStep1,
  onSubmitStep2,
  onRequestSession,
  onConfirmCorrections,
  step1Form,
  setStep1Form,
  step2Form,
  setStep2Form,
  submitting,
  addPastExperience,
  removePastExperience,
  updatePastExperience
}) {
  const isLocked = step.status === 'locked';
  const isCompleted = step.status === 'completed';
  const isActiveStep = step.status === 'active';
  const isPendingReview = step.submission?.status === 'pending_review' || step.submission?.status === 'requested';

  const getStatusBadge = () => {
    if (isCompleted) return <Badge className="bg-green-500">Completed</Badge>;
    if (isPendingReview) return <Badge className="bg-amber-500">Pending Review</Badge>;
    if (isActiveStep) return <Badge className="bg-blue-500">Active</Badge>;
    if (isLocked) return <Badge variant="outline" className="text-slate-500">Locked</Badge>;
    return null;
  };

  const getStepIcon = () => {
    if (isCompleted) return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (isLocked) return <Lock className="h-5 w-5 text-slate-500" />;
    return <span className="text-white font-bold">{stepNum}</span>;
  };

  return (
    <Card className={`
      ${isLocked ? 'bg-slate-800/50 border-slate-700' : ''}
      ${isActiveStep ? 'bg-slate-800 border-amber-500/50' : ''}
      ${isCompleted ? 'bg-slate-800 border-green-500/30' : ''}
      ${isPendingReview ? 'bg-slate-800 border-amber-500/30' : ''}
    `}>
      <div 
        className={`p-4 flex items-center justify-between cursor-pointer ${isLocked ? 'opacity-60' : ''}`}
        onClick={!isLocked ? onToggle : undefined}
      >
        <div className="flex items-center gap-4">
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center
            ${isCompleted ? 'bg-green-500/20' : ''}
            ${isActiveStep ? 'bg-amber-500' : ''}
            ${isLocked ? 'bg-slate-700' : ''}
            ${isPendingReview ? 'bg-amber-500/20' : ''}
          `}>
            {getStepIcon()}
          </div>
          <div>
            <h3 className="text-white font-semibold">{step.name}</h3>
            <p className="text-slate-400 text-sm">Step {stepNum > 2 ? stepNum - 1 : stepNum}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge()}
          {!isLocked && (
            <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${isActive ? 'rotate-90' : ''}`} />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isActive && !isLocked && (
        <CardContent className="pt-0 pb-6 border-t border-white/10">
          {/* Step 1 Form - Portfolio Detail Submission (was Step 2) */}
          {stepNum === 1 && isActiveStep && !isPendingReview && (
            <form onSubmit={onSubmitStep2} className="mt-4 space-y-4">
              {/* Drive Access Instruction */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-amber-300 text-sm font-medium">
                  Ensure all Google Drive folders/files have open access (Anyone with the link can view)
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Professional Photos (Drive Link)</Label>
                  <Input
                    type="url"
                    required
                    value={step2Form.professional_photos_link}
                    onChange={(e) => setStep2Form({...step2Form, professional_photos_link: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="https://drive.google.com/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Video Resume (1.30 min - Drive Link)</Label>
                  <Input
                    type="url"
                    required
                    value={step2Form.video_resume_link}
                    onChange={(e) => setStep2Form({...step2Form, video_resume_link: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="https://drive.google.com/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Certificates (Drive Link)</Label>
                  <Input
                    type="url"
                    required
                    value={step2Form.certificates_link}
                    onChange={(e) => setStep2Form({...step2Form, certificates_link: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="https://drive.google.com/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Market Research PDF (Drive Link)</Label>
                  <Input
                    type="url"
                    required
                    value={step2Form.market_research_link}
                    onChange={(e) => setStep2Form({...step2Form, market_research_link: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="https://drive.google.com/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">B2B SaaS Sales Playbook (Google Doc)</Label>
                  <Input
                    type="url"
                    required
                    value={step2Form.sales_playbook_link}
                    onChange={(e) => setStep2Form({...step2Form, sales_playbook_link: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="https://docs.google.com/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Cold Call Recording (Drive Link)</Label>
                  <Input
                    type="url"
                    required
                    value={step2Form.cold_call_recording_link}
                    onChange={(e) => setStep2Form({...step2Form, cold_call_recording_link: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="https://drive.google.com/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">LinkedIn Profile URL</Label>
                  <Input
                    type="url"
                    required
                    value={step2Form.linkedin_profile_url}
                    onChange={(e) => setStep2Form({...step2Form, linkedin_profile_url: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Resume PDF (Drive Link)</Label>
                  <Input
                    type="url"
                    required
                    value={step2Form.resume_link}
                    onChange={(e) => setStep2Form({...step2Form, resume_link: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="https://drive.google.com/..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Contact Email</Label>
                  <Input
                    type="email"
                    required
                    value={step2Form.contact_email}
                    onChange={(e) => setStep2Form({...step2Form, contact_email: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Contact Phone</Label>
                  <Input
                    type="tel"
                    required
                    value={step2Form.contact_phone}
                    onChange={(e) => setStep2Form({...step2Form, contact_phone: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Why Hire Me (1-2 paragraphs)</Label>
                <Textarea
                  required
                  value={step2Form.why_hire_me}
                  onChange={(e) => setStep2Form({...step2Form, why_hire_me: e.target.value})}
                  className="bg-white/10 border-white/20 text-white"
                  placeholder="Explain why companies should hire you..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Past Performance Report (Achievements, Metrics, Awards)</Label>
                <Textarea
                  required
                  value={step2Form.past_performance_report}
                  onChange={(e) => setStep2Form({...step2Form, past_performance_report: e.target.value})}
                  className="bg-white/10 border-white/20 text-white"
                  placeholder="Describe your past performance metrics..."
                  rows={3}
                />
              </div>
              
              {/* Past Experience - Multiple Entries */}
              <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-slate-300 font-medium">Past Experience</Label>
                  <Button
                    type="button"
                    onClick={addPastExperience}
                    size="sm"
                    className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30"
                    data-testid="add-experience-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Experience
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {step2Form.past_experiences.map((exp, index) => (
                    <div key={index} className="relative bg-slate-800/50 rounded-lg p-4 border border-white/5">
                      {step2Form.past_experiences.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePastExperience(index)}
                          className="absolute top-2 right-2 text-slate-500 hover:text-red-400 transition-colors"
                          data-testid={`remove-experience-${index}-btn`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                      <div className="flex items-center gap-2 mb-3">
                        <Briefcase className="h-4 w-4 text-amber-500" />
                        <span className="text-slate-400 text-sm font-medium">Experience {index + 1}</span>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-slate-400 text-sm">Company Name</Label>
                          <Input
                            value={exp.company}
                            onChange={(e) => updatePastExperience(index, 'company', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="Acme Corp"
                            data-testid={`experience-${index}-company`}
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-sm">Role</Label>
                          <Input
                            value={exp.role}
                            onChange={(e) => updatePastExperience(index, 'role', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="Sales Executive"
                            data-testid={`experience-${index}-role`}
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-sm">Duration (e.g., January 2022 to March 2023)</Label>
                          <Input
                            value={exp.duration}
                            onChange={(e) => updatePastExperience(index, 'duration', e.target.value)}
                            className="bg-white/10 border-white/20 text-white"
                            placeholder="2 years"
                            data-testid={`experience-${index}-duration`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Hobbies & Interests (1-2 paragraphs)</Label>
                <Textarea
                  required
                  value={step2Form.outside_work}
                  onChange={(e) => setStep2Form({...step2Form, outside_work: e.target.value})}
                  className="bg-white/10 border-white/20 text-white"
                  placeholder="What do you do outside of work?"
                  rows={2}
                />
              </div>

              <Button type="submit" disabled={submitting} className="bg-amber-500 hover:bg-amber-600">
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Submit Portfolio Details
              </Button>
            </form>
          )}

          {/* Step 2 - Admin Review (View Only) */}
          {stepNum === 2 && (
            <div className="mt-4">
              <p className="text-slate-400">
                Your submissions are being reviewed by the admin. You'll be notified once the review is complete.
              </p>
              {step.admin_review && (
                <div className="mt-4 p-4 bg-white/5 rounded-lg">
                  <p className="text-white font-medium">Admin Feedback:</p>
                  <p className="text-slate-300 mt-2">{step.admin_review.comments || 'No comments'}</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3 - One-on-One Session Request */}
          {stepNum === 3 && isActiveStep && !step.submission?.requested_at && (
            <div className="mt-4">
              <p className="text-slate-400 mb-4">
                Request a one-on-one session with the admin to review your portfolio and discuss your placement journey.
              </p>
              <Button onClick={onRequestSession} disabled={submitting} className="bg-amber-500 hover:bg-amber-600">
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <User className="mr-2 h-4 w-4" />}
                Request 1:1 Session
              </Button>
            </div>
          )}
          
          {stepNum === 3 && step.submission?.status === 'requested' && (
            <div className="mt-4 p-4 bg-amber-500/10 rounded-lg">
              <p className="text-amber-400">
                Session requested on {new Date(step.submission.requested_at).toLocaleString()}
              </p>
              <p className="text-slate-400 mt-2">Waiting for admin to schedule and complete the session.</p>
            </div>
          )}

          {/* Step 4 - Confirm Corrections */}
          {stepNum === 4 && isActiveStep && !step.submission?.corrections_completed && (
            <div className="mt-4">
              <p className="text-slate-400 mb-4">
                Confirm that you have completed all corrections as discussed in your 1:1 session.
              </p>
              <Button onClick={onConfirmCorrections} disabled={submitting} className="bg-amber-500 hover:bg-amber-600">
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                Yes, Corrections Completed
              </Button>
            </div>
          )}

          {stepNum === 4 && step.submission?.status === 'pending_verification' && (
            <div className="mt-4 p-4 bg-amber-500/10 rounded-lg">
              <p className="text-amber-400">Corrections confirmed. Waiting for admin verification.</p>
            </div>
          )}

          {/* Show submission details if pending review */}
          {isPendingReview && stepNum === 1 && (
            <div className="mt-4 p-4 bg-amber-500/10 rounded-lg">
              <p className="text-amber-400 font-medium">Submission Pending Review</p>
              <p className="text-slate-400 text-sm mt-1">
                Submitted on {new Date(step.submission.submitted_at).toLocaleString()}
              </p>
            </div>
          )}

          {/* Show admin review if rejected - with resubmit option */}
          {step.submission?.status === 'reject' && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 font-medium">Submission Rejected</p>
              <p className="text-slate-300 mt-2">{step.admin_review?.comments || 'Please revise and resubmit.'}</p>
              <p className="text-amber-400 mt-3 text-sm">You can resubmit your documents above.</p>
            </div>
          )}

          {/* Show resubmit option if needs_resubmit is true */}
          {step.submission?.needs_resubmit && (
            <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-amber-400 font-medium">Resubmission Required</p>
              <p className="text-slate-300 mt-2">Please review the feedback and resubmit your documents.</p>
            </div>
          )}

          {/* Completed step info */}
          {isCompleted && (
            <div className="mt-4 p-4 bg-green-500/10 rounded-lg">
              <p className="text-green-400 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Step completed successfully!
              </p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
